import type { CSSObject } from '@mui/material/styles'

/*
  PER-ICON HOVER MOTION FOR THE PLATFORM'S NAVIGATION SURFACES.

  A nav row's icon greets the pointer with a short, one-shot movement that says what
  that icon MEANS: the Certificates badge turns and its check is written on, stroke
  first to last; the Analytics bars dip and rise; the Studio palette's wells are sampled
  in turn around its rim; the People pair gains its second figure; the Features toggle throws to
  its "on" position. One generic wobble applied to thirty glyphs would be motion without
  information — the whole point is that the movement is a second reading of the icon.

  A COROLLARY LEARNED THE HARD WAY, worth stating because it is the thing to check when
  a recipe reads as weak: NO TWO UNRELATED ICONS MAY SHARE A MOTION. The first pass had
  Tags and Products both on one `swing` and Help on the same generic `turn` as Settings,
  and those were exactly the rows that came back as flat — a shared motion says "this is
  a nav row", which the user already knows, rather than "this is Tags". Sharing is
  legitimate only where the icon is literally the same glyph for the same object
  (ShoppingBag on Products and Orders, Users on People and Users, LifeBuoy on the
  internal Support row and the Settings Company -> Support row).

  ── THREE SURFACES, ONE TABLE, ONE FACTORY ──────────────────────────────────────
  This started as the sidebar rail's file, living in `Sidebar/`. The Settings left nav
  (`pages/settings/SettingsPage`) is the second consumer, which is exactly the moment
  §36 says to extract rather than to import across feature folders or copy-paste. So
  the module moved one level up, to `components/navigation/`, where both a component
  and a page can reach it without either owning it. The sidebar's ACCOUNT CHIP MENU
  (`Sidebar/components/SidebarUserBlock`) is the third — see `accountMenuIconMotionSx`
  at the foot of this file for why that menu and no other.

  What actually differs between the surfaces is ONE STRING. The rail's rows are MUI
  `ListItemButton`s; the Settings nav's and the account menu's rows are MUI `MenuItem`s
  (see @repo/ui's `SideNav`). Everything else — the trigger set, the inert rules, the
  recipes, the reduced-motion kill — is identical, so the surface-specific part is a
  parameter (`navIconMotionSx(rowSelector)`) and not a fork.

  ── WHY THIS IS ONE STYLE OBJECT AND NOT A PROP ON EACH NAV ITEM ────────────────
  The rail's rows are built by six different components (SidebarList x3 lists, the
  Search trigger, the Help popover's trigger row, the Collapse toggle, the encoding
  device button, the user block), and the Settings nav's rows are built by a shared
  `SideNav` that knows nothing about product vocabulary. Threading a `motion` prop
  through all of them would put the same decision in seven places and leave the eighth
  — whichever row is added next — silently un-animated. So this is applied ONCE, on a
  container, and keys off the class lucide already stamps on every icon it renders:

      <svg class="lucide lucide-badge-check"> ...

  lucide derives that class from the icon's NAME, so it is a stable, semantic handle that
  survives lucide reordering an icon's internal paths. That is the same reasoning the
  MuiListItemButton override in @repo/ui's theme gives for owning the rail's hover colour
  centrally rather than per component.

  Under lucide-react v1 (VRG-629) an icon stamps `lucide-<name>` AND `lucide-<alias>` for
  each of its aliases — `CircleHelp` now renders `circle-question-mark` and carries
  `lucide-circle-help` as an alias class, which is the one this file keys on. (Under 0.x
  the class was the kebab-case of the imported component name instead; see `building-2`
  below for the one recipe that difference broke.) The guard checks every class named here
  against what lucide actually emits.

  SCOPE THE CONTAINER TIGHTLY, and note it matters more for MenuItem than for
  ListItemButton. The rail applies this to the Drawer, so it cannot leak into
  SearchSelect's option list (also ListItemButton) or into the Help popover's MENU
  (which portals out of the rail). `.MuiMenuItem-root` is the commoner class of the
  two — every Select dropdown in the app renders MenuItems — so the Settings nav
  applies it to the nav COLUMN, not to the page. Most dropdowns portal to the body and
  would escape anyway, but "most" is not an argument to widen the scope.

  ── WHY IT LIVES HERE AND NOT IN @repo/ui (working-conventions §47) ─────────────
  Layer 1 holds cross-app PRIMITIVES. This table is product vocabulary — Certificates,
  Analytics, Invoices, Demos, Roles & permissions, Field mapping — for navigation that
  exists only in apps/platform. A `lucide-badge-check` -> "turn the badge" mapping in
  the shared base would be putting one app's information architecture into the layer
  both apps import. The second consumer strengthens rather than weakens that: both
  consumers are platform surfaces, so the shared home is platform's own component
  layer (layer 2), which is precisely where §47 says an app-specific composition goes.

  (A related §47 note, for anyone who does later move a piece of this down a layer:
  layer-1 components may not use `theme.spacing()`, because the two apps have
  different spacing bases. Nothing here uses it — every number below is px or degrees
  in the icon's own 24x24 coordinate space — so the constraint is already satisfied.)

  ── EVERY ICON MOVES, INCLUDING THE ONES WITH NO RECIPE ─────────────────────────
  `pop` is the fallback for any lucide glyph in a nav. A nav where some rows animate
  and others sit dead reads as unfinished, for the same reason §21 requires every kebab
  row to carry an icon: the inconsistency is more noticeable than either state on its
  own. A new nav item is therefore animated the day it is added.

  That is a floor, not a target. Every one of the 17 glyphs in the Settings nav has its
  own recipe below; the fallback exists for the row that has not been thought about yet.

  ── THE FOUR HARD CONSTRAINTS ───────────────────────────────────────────────────
  1. SUBTLE, BUT CALIBRATED RATHER THAN MINIMAL. The keyframes START AND END AT REST, so
     the icon settles while the pointer is still on the row — a greeting, not a state.
     Nothing loops, nothing is left transformed, and there is no reverse animation to
     unwind on mouse-out. Most recipes run 300-620ms; the Certificates gesture is
     deliberately the long one at 860ms (see below).

     THE AMPLITUDE BAND IS ~4.5-7.5px OF PEAK TRAVEL on the 24px glyph, and it is a
     MEASURED band, not a taste. An earlier pass aimed at "~2px" and produced four
     recipes the user could not see at all — Help measured 1.04px and was reported as
     having no animation whatsoever, when it had been firing correctly the whole time.
     The band comes from the two recipes that were approved on sight: Analytics at
     7.19px and Tags at 4.87px.

     MEASURE TRAVEL, NOT THE PARAMETER. An angle is an amplitude per unit of RADIUS, and
     a scale is an amplitude per unit of SIZE, so neither number means anything on its
     own: 11 degrees about a glyph's own centre bought 1px, and 1.9x on a 2px dot bought
     0.64px. The honest metric is how far the rendered geometry actually moves. To take
     it: hover the row, pause with `animation-play-state: paused`, then walk each
     animation's `currentTime` and diff `getBoundingClientRect()` against rest.

     AND CHECK THE SHAPE'S SYMMETRY BEFORE REACHING FOR A BIGGER ANGLE. The Certificates
     badge is eight identical arcs, so it maps onto itself every 45 degrees and 22 is
     already the most rotation it can express — see `veraNavBadgeTurn`. `contrast` and
     `toggle-left` are the Settings nav's two instances of the same trap and neither one
     rotates: Contrast's ring is a circle, so a rotating glyph would move nothing the eye
     can see, and it wipes instead; ToggleLeft's track is a stadium, so its knob
     translates.

     THE VIEWBOX CLIPS CHILD RECIPES ONLY — NOT WHOLE-GLYPH ONES, AND CONFUSING THE TWO
     COSTS REAL AMPLITUDE. lucide's `<svg>` takes the UA default `overflow: hidden`
     (nothing in this app resets it), so a transform on a PATH/CIRCLE/LINE pushes geometry
     against that edge and anything past it is CUT. A transform on the `<svg>` ELEMENT
     moves the whole element in the page instead: the geometry never moves relative to its
     own viewBox, so nothing is clipped at any amplitude.

     This was got wrong on the first pass, in the expensive direction. Four recipes here
     (`bell`, `map-pin`, `ticket`, `user`) were written and commented as if a viewBox
     ceiling bound them, and three of the four had their amplitude chosen to respect a
     limit that does not exist. Measured with the child geometry diffed against the svg's
     own box, all four overflow by exactly 0.00px. Their numbers are now justified by how
     the movement READS in a 38px row beside a 12px label gap — a real constraint — and
     not by a clip that cannot happen.

     Where the ceiling genuinely binds, it is a child recipe and the measurement says so:
     `gift` clips 0.26px at its current setting (the bow's loops are the highest geometry
     in the glyph), and `blocks` clips at 3.4px of travel and not at 3.0. Both were tuned
     by sweeping candidates in the browser and reading the overflow, which is the only way
     to get this right — the arithmetic estimate for `blocks` said it had 2.5 units of
     headroom, and it has more than 3.

     `sliders-horizontal` is the one recipe still sized by pure geometry rather than by
     either constraint: each handle moves exactly the 4px width of its own track gap.

     Where a recipe lands under the band, the reason is recorded on it. Do not "fix" one of
     those by raising the number without re-measuring the overflow.

  2. TRANSFORM AND OPACITY, WITH EXACTLY ONE DOCUMENTED EXCEPTION. No width/height/
     top/left anywhere. The exception is the Certificates check, which animates
     `stroke-dashoffset` — a paint-level property, not a composited one. That is a
     deliberate, bounded trade: it is ONE path, on ONE icon, only while that single row
     is hovered, and it is the only way to draw a stroke along its own length. The
     constraint it is written against is "do not animate stroke-dasharray across many
     icons at once", and one path on one hover is the case that constraint exempts.
     Do not extend the technique to a second icon without re-measuring. `shield-check`
     is the icon that would most obviously want it and deliberately does NOT get it —
     see its recipe.
     `will-change` is NOT set — hinting ~30 permanently-composited layers to save one
     sub-second animation is the wrong trade.
  3. HOVER **AND** :focus-visible. Keyboard traversal of a nav gets the same affordance
     the pointer does. Note the three shapes: SidebarList wraps its button in a
     react-router <Link>, so focus can land on the <a>; the rail's button-only rows
     (Search, Help, Collapse) take it on the ListItemButton itself; the Settings nav's
     MenuItems take it directly. All three are covered.
  4. prefers-reduced-motion: reduce KILLS ALL OF IT, at the bottom of this object.

  ── THREE KINDS OF ROW HOLD STILL, FOR THREE DIFFERENT REASONS ──────────────────
  Every trigger carries `NOT_INERT`, which excludes all three.

  1. THE SELECTED ROW. The theme's MuiListItemButton override makes it inert under
     hover on purpose — its docblock: "SELECTED stays bright. It is the answer to
     'where am I', which must not depend on where the pointer happens to be." Motion is
     a second hover channel, so animating the selected row would reinstate exactly the
     hover-response that override removed. The row you are already on also has nothing
     to offer a click. This applies verbatim to the Settings nav, whose selected row is
     the only marker of which section you are reading.

  2. CONTROLS, marked `data-nav-control`. A CONTROL ACTS ON THE RAIL; A DESTINATION
     GOES SOMEWHERE. Collapse operates the sidebar itself, so there is no thing being
     represented for a motion to express — a greeting animation on it is decoration
     with no referent, which is precisely what this file exists to avoid. Nav
     destinations animate; chrome that operates the chrome does not.

     The marker sits on the ROW, not the icon, and that is load-bearing: the Collapse
     row holds TWO glyphs (the panel icon and the auto-lock button), and suppressing by
     icon name would have silenced one and left the other greeting the pointer. It also
     means a control row added later opts out by carrying one attribute, rather than by
     someone remembering to add its glyph to a list in this file.

     NOTE WHAT THIS DOES NOT COVER. Search is a control by the same reading — it opens
     an overlay rather than routing — and it KEEPS its motion, because the request was
     specifically that Search feel better, and it now sweeps. The rule is a default for
     new chrome, not a licence to strip motion from a row someone asked to improve.
     "Reader connected" is the other borderline row and is deliberately left animated
     rather than silently reclassified.

  3. PERMISSION-LOCKED ROWS, `.Mui-disabled`. The Settings nav renders a section the
     user cannot view as a disabled row with a tooltip naming the permission (§27/§46).
     A greeting animation on a row that cannot be clicked promises an affordance that
     is not there, which is the same defect as animating the selected row from the
     opposite direction. MUI already sets `pointer-events: none` on a disabled MenuItem
     so `:hover` cannot match — this is the belt to that braces, and it also covers
     `:focus-visible`, which CAN still land on a disabled row in some browsers.
*/

/**
 * The icons whose motion reaches INSIDE the glyph, and the child-element signature
 * each one is written against.
 *
 * KEEP THIS TABLE IN STEP WITH THE RECIPES IN BOTH DIRECTIONS. An icon that stops
 * sub-targeting must come OUT — `tag` was pinned while its grommet flashed, and the
 * moment that recipe was replaced by a whole-glyph flip the entry became a build gate
 * on structure nothing reads. A guard that fails over an assumption no code makes is
 * how guards get deleted wholesale.
 *
 * BE HONEST ABOUT THE EXPOSURE. lucide renders an icon's children from a plain array
 * and puts NOTHING identifying in the DOM — the `key` in its source is a React key, not
 * an attribute. So a sub-element recipe has only positional selectors to work with, and
 * a future lucide release that reorders an icon's paths would silently retarget it.
 *
 * THAT IS NOT HYPOTHETICAL. The lucide-react 0.x -> 1.47 bump (VRG-629) reordered or
 * redrew the children of THIRTEEN of the icons below — `plug`'s body moved between its
 * prongs, both receipts moved their outline from first to last, the Controls handles and
 * Analytics bars became `path`s, the toggle knob moved, the Certificates check was
 * redrawn in the opposite direction. The recipes were re-mapped child by child against
 * the v1 source; the comment on each says which index is which part.
 *
 * So each child is pinned by TAG AND BY LUCIDE'S CONTENT KEY (`'path#xofnsj'`). The key
 * is a hash of the element's attributes: it travels with the element through a reorder
 * and changes when its geometry changes, so the guard sees both. Tags alone could not see
 * a reorder among same-type children at all — that version of this table would have
 * passed `plug`, `receipt` and `receipt-text` above while their recipes animated the
 * wrong parts. The comment after each pin names the part, so a mismatch can be re-mapped
 * without opening lucide's source.
 *
 * Read the selectors in three groups, by what a reorder WOULD do to them:
 *
 *   - `blocks` (rect), `circle-help` (its two paths), `contrast` (its one path) and
 *     `toggle-left` (its one circle) name a whole element TYPE rather than a position.
 *     Immune to reordering, but their recipes carry coordinates (an origin, a throw), so
 *     a redrawn child still matters — v1 moved the toggle knob and grew the block.
 *   - `palette` and `calculator` use `:nth-of-type` within a run of like parts (4 wells /
 *     7 round keys), so a reorder there only permutes which part leads the stagger. But
 *     calculator's third target, `line:nth-of-type(2)`, has the DISPLAY as its only other
 *     `line`: swap those and the readout thickens instead of the equals key.
 *   - Everything else is genuinely order-dependent, because the parts its recipe tells
 *     apart are all the same tag. Since v1 that includes `chart-no-axes-column`,
 *     `sliders-horizontal` (handles and tracks are all nine `path`s now, so a reorder can
 *     slide a TRACK) and `log-out` (its arrow was a `polyline` + `line`; it is two paths
 *     beside the room's path).
 *
 * Hence this table and `scripts/check-nav-icon-motion.mjs`, which reads the actual
 * lucide package and FAILS THE BUILD on any mismatch. A convention that has to be
 * remembered across a dependency bump is not a control (§41, §45); the guard is.
 *
 * The whole-svg recipes below need no entry here — they key only on the icon's class
 * and are unaffected by anything inside it. The guard still checks that their CLASS
 * exists, and that every icon a recipe reaches inside has an entry here.
 */
export const SUB_TARGETED_ICON_SIGNATURES = {
  'arrow-left-right': [
    'path#9rb6wj', // left arrowhead
    'path#6tx8e3', // top bar
    'path#siv7j2', // right arrowhead
    'path#h6l3hr', // bottom bar
  ],
  'badge-check': [
    'path#3c2336', // badge outline
    'path#xofnsj', // check — must match CHECK_PATH_D
  ],
  'blocks': [
    'path#1ah6g2', // the tray of three blocks
    'rect#88lufb', // the detached block, (14,2) 8x8
  ],
  'calculator': [
    'rect#1nb95v', // body
    'line#x4nwl0', // display
    'line#wjye3r', // tall equals key, (16,14)-(16,18)
    'path#1m94wz', // key (16,10)
    'path#1nrarc', // key (12,10)
    'path#19clt8', // key (8,10)
    'path#1etili', // key (12,14)
    'path#6423bh', // key (8,14)
    'path#mhygvu', // key (12,18)
    'path#lrp35t', // key (8,18)
  ],
  'chart-no-axes-column': [
    'path#1hz6c0', // bar x=5, the short left one
    'path#1lcnhd', // bar x=12, the tall middle one
    'path#unv183', // bar x=19, right
  ],
  'circle-help': [
    // Renders as `circle-question-mark`; `circle-help` is its alias class.
    'circle#1mglay', // ring
    'path#1u773s', // hook
    'path#p32p05', // dot (12,17)
  ],
  'contrast': [
    'circle#1mglay', // ring
    'path#j4l70d', // half-disc
  ],
  'gift': [
    'path#1akyts', // spine
    'path#1sqzm4', // box body
    'path#kc0143', // bow
    'rect#1hberx', // lid, y 7..11
  ],
  'hand-coins': [
    'path#geh8rc', // hand
    'path#1fto5m', // hand
    'path#1pfhp9', // wrist
    'circle#1n0dlu', // coin (16,9)
    'circle#151irh', // coin (6,5)
  ],
  'history': [
    // Renders as `rotate-ccw-clock`; `history` is its alias class.
    'path#1357e3', // arc
    'path#1xhq8a', // arrowhead
    'path#1fdv2h', // hands
  ],
  'log-out': [
    'path#1bji2h', // arrowhead
    'path#dn1m92', // shaft
    'path#1uf3rs', // the room
  ],
  'palette': [
    'path#e79jfc', // palette outline
    'circle#1okk4w', // well (13.5,6.5) — top
    'circle#f64h9f', // well (17.5,10.5) — right
    'circle#qy21gx', // well (6.5,12.5) — lower left
    'circle#fotxhn', // well (8.5,7.5) — upper left
  ],
  'plug': [
    'path#1ega77', // cord
    'path#18g5xt', // right prong
    'path#1xoxul', // body
    'path#14iosj', // left prong
  ],
  'receipt': [
    'path#pyj7ub', // "$" vertical bar
    'path#1elt7d', // "$" S curve
    'path#ycz6yz', // torn-edge outline
  ],
  'receipt-text': [
    'path#wsln4y', // text rule y=16 (bottom)
    'path#1l3xfs', // text rule y=8 (top)
    'path#1fr5h0', // text rule y=12 (middle)
    'path#ycz6yz', // torn-edge outline
  ],
  'shield-check': [
    'path#oel41y', // shield
    'path#dzmm74', // check
  ],
  'sliders-horizontal': [
    'path#1qgfaw', // row 1 (y=5) left track
    'path#yhmn1j', // row 3 (y=19) left track
    'path#1sua03', // row 1 HANDLE, x=14
    'path#1q0r14', // row 3 HANDLE, x=16
    'path#1o4lsq', // row 2 (y=12) right track
    'path#1rlt1p', // row 3 right track
    'path#1oszz2', // row 1 right track
    'path#tgpxqk', // row 2 HANDLE, x=8
    'path#a7s4jb', // row 2 left track
  ],
  'toggle-left': [
    'circle#u3jwor', // knob, cx=9
    'rect#g7kal2', // track, x 2..22
  ],
  'users': [
    'path#1yyitq', // front body
    'path#16gr8j', // back head
    'path#kshegd', // back body
    'circle#nufk8', // front head
  ],
  // NOT pinned, deliberately, and the omissions are the interesting part: `bell`,
  // `building-2`, `map-pin`, `ticket` and `user` animate as WHOLE GLYPHS, so no
  // selector below reads their internals and a guard entry for them would be a build
  // gate on an assumption nothing makes — which the docblock above says is how guards
  // get deleted. Add one the moment a recipe reaches inside them, and not before.
} as const

/**
 * The Certificates check, and the length of the stroke that draws it.
 *
 * This is the ONE recipe that depends on an icon's GEOMETRY rather than just its
 * structure, so the path data is pinned alongside the signatures above and the guard
 * compares it against lucide's own source. Without that, lucide redrawing the check
 * would leave the structure check passing (still two paths) while the dash length
 * silently stopped matching — a check that draws only nine tenths of the way, or one
 * that is already complete before the animation starts.
 *
 * `m16 9-5.5 5.5L8 12` (lucide-react v1) is: move to (16,9), line to (10.5,14.5), line
 * to (8,12). So
 *
 *     sqrt(5.5^2 + 5.5^2) + sqrt(2.5^2 + 2.5^2) = 5.5*sqrt2 + 2.5*sqrt2 = 8*sqrt2 = 11.3137
 *
 * rounded UP to 11.32 so a full offset clears the path completely rather than leaving a
 * hair of stroke at the tip. (Arithmetic only for v1 — the 0.x value was confirmed
 * against `getTotalLength()` in the browser, and this one should be too.)
 *
 * ⚠️ THE DIRECTION IS REVERSED FROM 0.x, AND THE KEYFRAME COMPENSATES. The 0.x check was
 * `m9 12 2 2 4-4`, which started at the SHORT arm's tip — the order a person writes a
 * check — so walking a positive offset to zero drew it correctly for free. v1's path
 * starts at the LONG arm's tip and ends at the short one, so the same keyframe would
 * write the check backwards. `veraNavDraw` therefore starts from a NEGATIVE offset,
 * which reveals the stroke from the path's END: short arm, elbow, long arm, as before.
 * If lucide ever flips it back, flip the sign back with it.
 */
export const CHECK_PATH_D = 'm16 9-5.5 5.5L8 12'
export const CHECK_PATH_LENGTH = 11.32

/*
  The ways a nav row can be "engaged", and the ways it can be inert. Written once and
  expanded per rule by `on()` below, because getting `:focus-visible` onto thirty
  selectors by hand is how one of them ends up hover-only.

  `:focus-within` was rejected: it also fires on a MOUSE click and then stays lit after
  the click, which would leave an icon mid-greeting on the row you just navigated from.
  `:has(:focus-visible)` would collapse these to one selector but buys nothing here.
*/
const NOT_INERT = ':not(.Mui-selected):not(.Mui-disabled):not([data-nav-control])'

/**
 * The MUI row classes the two navs are built from.
 *
 * Exported so a call site names a constant rather than retyping a class string that
 * has to match what @repo/ui happens to render — the failure mode being a nav that
 * silently animates nothing because the selector matched no row.
 */
export const NAV_ROW_LIST_ITEM = '.MuiListItemButton-root'
export const NAV_ROW_MENU_ITEM = '.MuiMenuItem-root'

/** The three engaged states, for one surface's row class. */
const triggersFor = (row: string) => [
  `& ${row}${NOT_INERT}:hover`,
  `& ${row}${NOT_INERT}:focus-visible`,
  `& a:focus-visible ${row}${NOT_INERT}`,
]

/*
  Named recipes are written against `.lucide.lucide-x` rather than `.lucide-x` so they
  out-specify the `.lucide` fallback outright (0,5,0 vs 0,4,0) instead of depending on
  source order inside a merged emotion object.
*/
const icon = (name: string) => `.lucide.lucide-${name}`

/*
  Origins are given in viewBox units, which map 1:1 to px here: lucide renders at its
  default size 24 on a "0 0 24 24" viewBox, so the element box and the coordinate space
  are the same 24x24. `transform-box: view-box` is required on CHILD elements to make
  a px origin resolve in that coordinate space; `fill-box` instead resolves against the
  element's OWN bounding box, which is what the palette wells and receipt lines want
  (each part pivots on itself, with no per-part geometry hardcoded here).
*/
const viewBox = (origin: string): CSSObject => ({
  transformBox: 'view-box',
  transformOrigin: origin,
})
const fillBox = (origin: string): CSSObject => ({
  transformBox: 'fill-box',
  transformOrigin: origin,
})

/*
  Three easings, and the difference between them is what "fast" and "slow" actually
  mean here — more than duration does.

  EASE is heavily front-loaded (~90% of the distance in the first third), which is what
  makes a short movement feel like a flick. Right for a bar dipping or a block snapping.
  It is NOT right for anything meant to read as deliberate: the first Certificates badge
  ran 460ms on this curve and came back as "too fast", and the duration was only half
  the reason.

  EASE_DELIBERATE is symmetric ease-in-out — it accelerates and decelerates evenly, so
  the movement reads as intended rather than flung.

  EASE_DRAW is a stronger ease-in-out, for the check stroke specifically: handwriting
  accelerates out of the first stroke and decelerates into the last.
*/
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)'
const EASE_DELIBERATE = 'cubic-bezier(0.45, 0, 0.55, 1)'
const EASE_DRAW = 'cubic-bezier(0.65, 0, 0.35, 1)'

/**
 * The whole table, bound to one surface's row class.
 *
 * Everything except `rowSelector` is shared: the same keyframes, the same recipes, the
 * same reduced-motion kill. Two calls therefore emit two copies of the rule set, which
 * is the deliberate trade — the alternative is one selector list carrying both row
 * classes, which would make the sidebar's stylesheet also match the Settings nav's
 * rows and vice versa, i.e. exactly the leakage the container scoping exists to
 * prevent. The duplicated `@keyframes` are byte-identical and emotion dedupes them by
 * name, so the cost is a few hundred bytes of selector text, not two animations.
 */
export const navIconMotionSx = (rowSelector: string): CSSObject => {
  const TRIGGERS = triggersFor(rowSelector)
  /**
   * Expand one target into the hover/focus trigger set.
   *
   * `target` MAY carry commas — `gift`, `plug` and `receipt` each animate two children
   * that are not adjacent in lucide's array, so they cannot be written as one
   * `nth-of-type` range. That is why this splits rather than interpolating the string
   * whole: `${trigger} a, b` prefixes only the FIRST branch and leaves the second as a
   * bare, unscoped selector — which would animate that child on every render of the
   * glyph anywhere in the app, hover or not. Caught before it shipped; it is an easy
   * thing to reintroduce by "simplifying" this back to a `map`.
   */
  const on = (target: string, style: CSSObject): CSSObject => {
    const targets = target.split(',').map(t => t.trim())
    return {
      [TRIGGERS.flatMap(t => targets.map(one => `${t} ${one}`)).join(', ')]: style,
    }
  }

  return {
    // ── Keyframes ────────────────────────────────────────────────────────────────
    // All of them return to identity so nothing is left displaced.
    '@keyframes veraNavPop': {
      '0%': { transform: 'scale(1)' },
      '45%': { transform: 'scale(1.1)' },
      '100%': { transform: 'scale(1)' },
    },
    '@keyframes veraNavBob': {
      '0%': { transform: 'translateY(0)' },
      '40%': { transform: 'translateY(-2px)' },
      '100%': { transform: 'translateY(0)' },
    },
    '@keyframes veraNavTurn': {
      '0%': { transform: 'rotate(0deg)' },
      '55%': { transform: 'rotate(24deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },
    '@keyframes veraNavTilt': {
      '0%': { transform: 'rotate(0deg)' },
      '45%': { transform: 'rotate(-12deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },
    '@keyframes veraNavRise': {
      '0%': { transform: 'scale(1)' },
      '45%': { transform: 'scale(1.07)' },
      '100%': { transform: 'scale(1)' },
    },
    '@keyframes veraNavShout': {
      '0%': { transform: 'rotate(0deg) scale(1)' },
      '45%': { transform: 'rotate(-8deg) scale(1.07)' },
      '100%': { transform: 'rotate(0deg) scale(1)' },
    },
    // A bar dips and comes back rather than growing: growing past the viewBox would be
    // clipped by the svg's own overflow, so the readable direction is down-then-up.
    '@keyframes veraNavBar': {
      '0%': { transform: 'scaleY(1)' },
      '35%': { transform: 'scaleY(0.55)' },
      '100%': { transform: 'scaleY(1)' },
    },
    // Deliberately starts at 0.55, not 0: if a lucide reorder ever slipped this onto the
    // receipt's OUTLINE instead of a text line, the failure is a mild squish rather than
    // a vanished icon. The guard should catch it first; this is the second line.
    '@keyframes veraNavPrint': {
      '0%': { transform: 'scaleX(0.55)' },
      '60%': { transform: 'scaleX(1)' },
      '100%': { transform: 'scaleX(1)' },
    },
    // Integrations — the detached block snaps clear of the tray and settles back.
    //
    // AMPLITUDE RAISED 2026-08-25 from 1.5px to 3.0px, on a measurement. At 1.5 the
    // recipe moved 2.12px, the quietest thing in either nav and less than half the 4.5px
    // floor this file sets for itself; it was written before that floor existed. Same
    // gesture, same direction, calibrated.
    //
    // 3.0 IS THE EDGE AND IT WAS FOUND EMPIRICALLY, because the arithmetic was wrong: the
    // estimate said the block had 2.5 units of headroom, and the browser says otherwise.
    // Swept against the child's overflow past the svg box —
    //
    //     1.5px -> 2.12px travel, 0.00 clipped   (the old value)
    //     3.0px -> 4.24px travel, 0.00 clipped   <- taken
    //     3.4px -> 4.81px travel, 0.40 clipped
    //     3.8px -> 5.37px travel, 0.80 clipped
    //
    // — so 3.0 is the most this can move with nothing cut. Still a shade under the band;
    // the corner of a 7x7 block 3 units from two edges is the reason.
    //
    // CUT TO 2.0px BY lucide-react v1, which redrew the block as 8x8 at (14,2) — its right
    // and top edges are now 2 units from the svg's, not 3. Same metric, re-derived (the
    // overflow is linear in the translate here, and the model reproduces all four sweep
    // rows above to the hundredth):
    //
    //     2.0px -> 2.83px travel, 0.00 clipped   <- taken
    //     2.4px -> 3.39px travel, 0.40 clipped
    //     3.0px -> 4.24px travel, 1.00 clipped   (the 0.x value, on the v1 block)
    //
    // The PEAK FRAME is unchanged: the block's corner lands on the svg's top-right corner
    // exactly as it did at 3.0 in 0.x, it just starts closer to it. Travel is now well under
    // the band, and it should be re-measured in the browser and re-thought rather than
    // pushed — a bigger block with less room is a real constraint, not a stale number.
    //
    // This moves the sidebar's Integrations row too, which is correct: same glyph, same
    // object, one recipe.
    '@keyframes veraNavSnap': {
      '0%': { transform: 'translate(0, 0)' },
      '40%': { transform: 'translate(2px, -2px)' },
      '100%': { transform: 'translate(0, 0)' },
    },

    // Certificates, part one: a slow, even turn.
    //
    // 22 DEGREES IS A CEILING, NOT A CHOICE, AND MORE WOULD BE LESS. The badge is drawn as
    // eight identical arcs, so it maps onto itself every 45 degrees: at 45 it would look
    // exactly UNROTATED, and anything past ~22.5 starts travelling back toward that. Half a
    // period is the most rotation this shape can express, and 22 is already there.
    //
    // So when Certificates needed more amplitude, the angle was the one number that could
    // not supply it. `veraNavSwell` below adds it on a channel with no period instead.
    '@keyframes veraNavBadgeTurn': {
      '0%': { transform: 'rotate(0deg)' },
      '55%': { transform: 'rotate(22deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },
    // Certificates, part three: the whole seal swells as it turns and the check lands.
    // Scale is not periodic, so unlike the rotation it can be pushed. Applied to the SVG
    // rather than to either path, so the badge and the check grow together and the check
    // never looks small inside a badge that has outgrown it.
    '@keyframes veraNavSwell': {
      '0%': { transform: 'scale(1)' },
      '55%': { transform: 'scale(1.2)' },
      '100%': { transform: 'scale(1)' },
    },
    // Certificates, part two: the check writes itself. Offsetting a dash the length of
    // the whole path hides it; walking that offset to 0 reveals the stroke progressively.
    // Ends at 0, which is also `stroke-dashoffset`'s value when no animation applies — so
    // the finished state and the resting state are identical and there is nothing to
    // unwind on mouse-out.
    //
    // NEGATIVE, because lucide v1's check path runs long arm -> short arm (see
    // CHECK_PATH_D). A positive offset reveals from the path's START; a negative one
    // reveals from its END, which is the short arm — the end a person starts writing at.
    '@keyframes veraNavDraw': {
      '0%': { strokeDashoffset: -CHECK_PATH_LENGTH },
      '100%': { strokeDashoffset: 0 },
    },

    // Studio: one paint well rises and swells, then settles. Fired four times with an
    // increasing delay (see the recipe below), so the colours are sampled in turn around
    // the palette's rim.
    //
    // IT LIFTS AS WELL AS GROWS, because scale alone has almost no range at this size and
    // the measurements say so plainly. Each well is a filled r=.5 circle — a 1px box — so
    // its bbox corner barely moves however hard it is scaled:
    //
    //     scale 3.4 / no lift  ->  1.68px travel
    //     scale 4.0 / no lift  ->  2.10px travel
    //     scale 4.6 / no lift  ->  2.52px travel   (a 1px dot becoming 4.6px across)
    //
    // — i.e. the whole 4.5px floor is unreachable on scale alone without turning the wells
    // into blobs wider than the gaps between them. Lift is the channel with range, and
    // travel here is very nearly linear in it (~lift + 0.85px), so the amplitude is set by
    // the translate and the scale is left at the value that reads as a well brightening.
    //
    // 4.2px OF LIFT MEASURES 5.03px, mid-band. NOTHING CLIPS at any candidate swept — the
    // wells sit deep inside the glyph (the highest is at cy 6.5, and at peak its top edge
    // is still 0.7 units inside the viewBox), so unlike `gift` or `blocks` this child
    // recipe is bounded by how it READS and not by the svg's overflow:
    //
    //     lift 3.2 -> 4.05px travel, 0.00 clipped   (the old single-pick value, under band)
    //     lift 3.8 -> 4.63px travel, 0.00 clipped
    //     lift 4.2 -> 5.03px travel, 0.00 clipped   <- taken
    //     lift 4.6 -> 5.42px travel, 0.00 clipped
    //     lift 5.0 -> 5.81px travel, 0.00 clipped
    //
    // Above ~4.6 the top well starts crossing the palette's own rim on the way up, which
    // reads as paint escaping the palette rather than being lifted off it. That is the
    // real ceiling, and it is a judgment rather than a measurement — say so rather than
    // implying the browser chose it.
    '@keyframes veraNavSample': {
      '0%': { transform: 'translateY(0) scale(1)' },
      '45%': { transform: 'translateY(-4.2px) scale(2.6)' },
      '100%': { transform: 'translateY(0) scale(1)' },
    },

    // Search: the glass sweeps across a surface. Translation-dominant with a small tilt,
    // which is what separates it from every rotation-dominant recipe here.
    '@keyframes veraNavScan': {
      '0%': { transform: 'translateX(0) rotate(0deg)' },
      '30%': { transform: 'translateX(-1.8px) rotate(-6deg)' },
      '70%': { transform: 'translateX(1.2px) rotate(3deg)' },
      '100%': { transform: 'translateX(0) rotate(0deg)' },
    },

    // People: the second figure arrives from behind the first. No overshoot — a person
    // walking into frame does not bounce off the edge of it.
    '@keyframes veraNavArrive': {
      '0%': { opacity: 0, transform: 'translateX(4px)' },
      '100%': { opacity: 1, transform: 'translateX(0)' },
    },

    // Help: a life ring riding a swell — lifts, rolls, settles. Slower and softer than
    // the mechanical `veraNavTurn` it replaced, because buoyancy is not machinery.
    //
    // AMPLITUDE RAISED 2026-08-25 from (-1.8px, -7deg) to (-3.0px, -12deg), and the reason
    // is a measurement rather than a preference: rotation about the ring's own CENTRE is
    // the low-radius case this file's header warns about, so 7 degrees bought very little
    // and the whole recipe measured 3.42px — under the 4.5px floor, and the quietest thing
    // in the Settings nav once LifeBuoy joined it as Company -> Support. Same gesture, same
    // keyframe, calibrated: it now measures 5.69px. Whole-glyph, so nothing is clipped at
    // either setting. It moves the sidebar's internal Support row and the Help popover row
    // too; that is the point of a shared glyph.
    //
    // (3.42 is the replayed measurement of the OLD keyframes on the real element. An
    // earlier draft of this comment asserted "~2.9px" from arithmetic — close enough to be
    // convincing and still wrong, which is the reason this file keeps saying to measure.)
    '@keyframes veraNavBuoy': {
      '0%': { transform: 'translateY(0) rotate(0deg)' },
      '30%': { transform: 'translateY(-3px) rotate(-12deg)' },
      '65%': { transform: 'translateY(1.2px) rotate(6deg)' },
      '100%': { transform: 'translateY(0) rotate(0deg)' },
    },

    // Help: the question mark cocks its head, inside a ring that stays put. A PART
    // moving within a static parent, which is what keeps it distinct from every
    // whole-glyph rotation in the file.
    //
    // AN ANGLE IS NOT AN AMPLITUDE — IT IS AN AMPLITUDE PER UNIT OF RADIUS. The first
    // version rotated 11 degrees about the icon's centre, and the "?" sits ON that centre,
    // so the whole gesture moved the glyph 1.04px and the user reported Help as having no
    // animation at all. It was firing the entire time. The fix is mostly the ORIGIN (moved
    // to the dot, so the hook swings on a real radius) and only partly the angle.
    '@keyframes veraNavAsk': {
      '0%': { transform: 'rotate(0deg)' },
      '30%': { transform: 'rotate(-26deg)' },
      '65%': { transform: 'rotate(14deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },

    // Tags: the tag turns over, the way you flip one to read the other side.
    //
    // THE ONLY Y-AXIS MOTION IN EITHER NAV, and that is the point — see the recipe below
    // for why two Z-rotations in a row were the wrong idea rather than the wrong numbers.
    // `perspective()` is inside every keyframe including 0% and 100%, so the depth is
    // constant and there is no frame where it snaps on or off.
    '@keyframes veraNavFlip': {
      '0%': { transform: 'perspective(90px) rotateY(0deg)' },
      '45%': { transform: 'perspective(90px) rotateY(-48deg)' },
      '100%': { transform: 'perspective(90px) rotateY(0deg)' },
    },

    // Products: weight drops in. The bag sinks and squashes against its own rim, then
    // rebounds — a dip, where Packages' `veraNavBob` is a hop. Opposite direction,
    // opposite meaning: one is carried, the other is filled.
    //
    // THE TWO HALVES WERE CANCELLING. With the origin at the rim, scaleY(0.94) pulls the
    // bag's bottom edge UP by 1.1px while translateY(1.2px) pushes it DOWN by 1.2 — so the
    // bottom of the bag moved a net 0.06px and the whole recipe measured 1.38px, the
    // quietest in the file. Both numbers are now large enough that the sink outruns the
    // squash and the bottom edge visibly drops.
    '@keyframes veraNavLoad': {
      '0%': { transform: 'translateY(0) scaleY(1)' },
      '35%': { transform: 'translateY(3.8px) scaleY(0.85)' },
      '70%': { transform: 'translateY(-1.4px) scaleY(1.06)' },
      '100%': { transform: 'translateY(0) scaleY(1)' },
    },

    // Sales: the coins bounce in the palm. Both rise and settle; the second is a
    // beat behind, so they read as two objects rather than one rigid block moving.
    //
    // COINS ONLY — the hand stays put. The whole point of the glyph is a hand
    // HOLDING money, so animating the hand with them would move the frame of
    // reference and the coins would appear stationary relative to it. Same
    // reasoning as the Studio palette (one well rises, the palette does not).
    //
    // Up-then-settle rather than a symmetric bob: a coin dropped into a palm has
    // a direction, and an even sine reads mechanical.
    '@keyframes veraNavCoin': {
      '0%': { transform: 'translateY(0)' },
      '40%': { transform: 'translateY(-2.6px)' },
      '75%': { transform: 'translateY(0.7px)' },
      '100%': { transform: 'translateY(0)' },
    },

    // ── Settings nav keyframes ───────────────────────────────────────────────────
    //
    // Everything below was written for the Settings left nav (VRG-405). The rail's
    // vocabulary above was NOT reused for any of them: `blocks`, `users` and `life-buoy`
    // already had recipes and are the same glyphs for the same objects, so they carry
    // over; the other fourteen glyphs are new and each got its own gesture.

    // Fields — each arrow reaches out toward the side it points at, top first.
    //
    // scaleX, NOT translateX, and the viewBox ceiling is the whole reason. A shuttle
    // would have to move the left-pointing arrow LEFT, and its tip already sits at x=4
    // with a 0.5px stroke, so 3.2px is all it has before the head is cut. Scaling the
    // group about the FAR end instead sweeps the tip 16 * 0.38 = 6.08px through the same
    // space the icon already occupies — more travel, no clipping, and it reads as the
    // arrow extending toward its target rather than the whole row sliding.
    '@keyframes veraNavReach': {
      '0%': { transform: 'scaleX(0.62)' },
      '60%': { transform: 'scaleX(1)' },
      '100%': { transform: 'scaleX(1)' },
    },

    // Notifications — the bell rings: a damped swing about its crown, four beats
    // decaying, the way a struck bell actually settles.
    //
    // Whole-glyph, so the viewBox does not bind (measured overflow 0.00px) and 16 degrees
    // is a judgment about READING, not a ceiling: the pivot is the crown, the clapper
    // hangs 18 units below it, and the four decaying beats are what make it a bell rather
    // than a wobble. Measured peak travel 5.88px.
    //
    // Distinct from `veraNavLean` (Profile), the other pendulum: this one hangs FROM its
    // pivot and decays over four beats; that one stands ON its pivot and leans over two.
    '@keyframes veraNavRing': {
      '0%': { transform: 'rotate(0deg)' },
      '22%': { transform: 'rotate(-16deg)' },
      '52%': { transform: 'rotate(11deg)' },
      '78%': { transform: 'rotate(-5deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },

    // Company -> General — the building goes up. scaleY from its own footing, so it
    // grows the way a structure is built rather than zooming toward the viewer.
    //
    // Deliberately NOT `veraNavRise` (Companies' shopfront, uniform scale about the
    // base). Same family of origin, different gesture: rise is a small symmetric swell
    // that returns; this is an asymmetric grow-into-place that starts compressed. The
    // two are never on screen together, but the rule in this file's header is about the
    // vocabulary, not about co-visibility.
    //
    // 0.72 -> 1 about the footing moves the roofline height * 0.28 and cannot clip,
    // because every frame is SMALLER than rest except a 3% overshoot. In 0.x that was
    // (12,22) and a 20-unit building, 5.6px; lucide-react v1's building stands on y=21
    // and tops out at y=3, so it is 18 * 0.28 = 5.04px — still mid-band.
    '@keyframes veraNavErect': {
      '0%': { transform: 'scaleY(0.72)' },
      '62%': { transform: 'scaleY(1.03)' },
      '100%': { transform: 'scaleY(1)' },
    },

    // Appearance — the dark half wipes across the disc.
    //
    // ROTATION IS THE WRONG CHANNEL HERE and that is worth stating, because Contrast
    // looks like a thing that should turn. Its ring is a CIRCLE: rotating the glyph maps
    // the ring exactly onto itself, so only the inner half-disc would appear to move,
    // and it would read as a shape sliding inside a hole. Scaling the half-disc about the
    // vertical diameter instead makes the light/dark boundary sweep out from the centre
    // line — which is what changing appearance IS. 0 -> 1 moves the disc's right edge
    // the full 6px of its radius, the largest clean travel in the Settings nav.
    '@keyframes veraNavShade': {
      '0%': { transform: 'scaleX(0)' },
      '62%': { transform: 'scaleX(1)' },
      '100%': { transform: 'scaleX(1)' },
    },

    // Referrals — the lid is levered off, tipping more than it lifts.
    //
    // A CHILD recipe, so the viewBox genuinely binds here (unlike the whole-glyph ones —
    // constraint 1), and the BOW is what binds it: its loops are the highest geometry in
    // the glyph at y=3, and the lid group carries them.
    //
    // The split between lift and tilt was found by sweeping candidates in the browser and
    // reading the child overflow, not by arithmetic. Lift is the expensive channel and
    // tilt is the cheap one, because the lid has 3 units of room at each SIDE and almost
    // none above:
    //
    //     -2.8px /  -7deg  ->  3.89px travel, 0.60px clipped
    //     -2.2px /  -7deg  ->  3.29px travel, 0.00px clipped
    //     -2.4px / -10deg  ->  3.94px travel, 0.51px clipped
    //     -1.6px / -16deg  ->  4.01px travel, 0.26px clipped   <- taken
    //
    // So trading lift for tilt buys travel AND gives back most of the clip. It is still
    // the quietest recipe in the Settings nav and slightly under the band; the bow is the
    // reason, and it is a hard one. Do not raise the lift without re-measuring.
    //
    // LIFT CUT FROM -1.6px TO -0.6px BY lucide-react v1, which drew the whole lid group
    // (lid, bow) exactly ONE UNIT HIGHER — lid y 7..11, bow loops topping out at y=2. The
    // origin moved up with it, to the lid's new centre (12,9), so taking exactly that one
    // unit off the lift makes the PEAK FRAME identical, in absolute coordinates, to the
    // 0.x peak above: same 0.26px clip, same pose. Only the travel from rest shrinks.
    // Re-derived with the same corner-of-bbox metric (it reproduces all four rows above):
    //
    //     -1.6px / -16deg  ->  4.01px travel, 1.26px clipped   (the 0.x value, on v1)
    //     -0.6px / -16deg  ->  3.01px travel, 0.26px clipped   <- taken (0.x's peak pose)
    //      0px   / -20deg  ->  3.20px travel, 0.00px clipped   (pure tilt: no lift at all)
    //
    // The pure-tilt row is the better number and was NOT taken: with no lift the lid only
    // rocks about its centre, and its left end sinks 9*sin(20) = 3.1 units into the box
    // (the taken row sinks it 9*sin(16) - 0.6 = 1.9, the same as the approved 0.x pose) —
    // more pressed-down than opened. A judgment call, made to keep the approved pose
    // rather than to invent a new one; re-measure in the browser and revisit.
    '@keyframes veraNavUnwrap': {
      '0%': { transform: 'translateY(0) rotate(0deg)' },
      '40%': { transform: 'translateY(-0.6px) rotate(-16deg)' },
      '100%': { transform: 'translateY(0) rotate(0deg)' },
    },

    // Activity log — the clock runs backwards.
    //
    // The hands, not the glyph. History's arc and its arrowhead are the "back" part of
    // the icon and already point the way; the hands are the part with a radius to swing
    // on. -80 degrees about the pivot moves the minute tip 2 * 5 * sin(40) = 6.43px, and
    // nothing leaves the box because the hands are wholly inside the dial.
    '@keyframes veraNavRewind': {
      '0%': { transform: 'rotate(0deg)' },
      '55%': { transform: 'rotate(-80deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },

    // Address book — the pin drops onto the map and plants.
    //
    // It STARTS displaced, like `veraNavArrive` and `veraNavPrint`: the pin is above the
    // row at t=0 and falls into place, rather than jumping up and coming back. Whole-glyph,
    // so nothing is clipped on the way down (measured overflow 0.00px) — the pin is simply
    // 4.5px higher in the ROW for the opening frames.
    //
    // The squash is about the TIP, so the pin compresses onto the point it just planted
    // rather than shrinking toward its middle. Measured peak travel 4.50px.
    '@keyframes veraNavDrop': {
      '0%': { transform: 'translateY(-4.5px) scaleY(1)' },
      '58%': { transform: 'translateY(0) scaleY(0.88)' },
      '80%': { transform: 'translateY(0) scaleY(1.05)' },
      '100%': { transform: 'translateY(0) scaleY(1)' },
    },

    // Installation — the plug goes into the socket.
    //
    // A PAIR THAT ONLY MEANS SOMETHING TOGETHER — the one such pair left in this file,
    // since Studio's pick/recede was the other and has been replaced. The body and
    // cord rise 4.5px while the prongs compress to a quarter of their length about their
    // own base — so the prong TIPS stay exactly where they are and the plug visibly
    // seats onto them. Translating the whole glyph instead would have pushed the tips
    // 4.5px past the top of the viewBox and cut them off, which is both a clip and the
    // wrong story: a plug going in does not take the socket with it.
    //
    // Same duration, same easing, no delay between them. Stagger them and the plug
    // detaches from its own prongs.
    '@keyframes veraNavPlugIn': {
      '0%': { transform: 'translateY(0)' },
      '48%': { transform: 'translateY(-4.5px)' },
      '100%': { transform: 'translateY(0)' },
    },
    '@keyframes veraNavProng': {
      '0%': { transform: 'scaleY(1)' },
      '48%': { transform: 'scaleY(0.25)' },
      '100%': { transform: 'scaleY(1)' },
    },

    // Billing — the amount lifts off the bill.
    //
    // The receipt's outline holds and the "$" inside it rises and grows: the charge
    // leaving the document. Distinct from `receipt-text`'s `veraNavPrint` (Invoices),
    // where the text lines print IN from the left and the subject is the document being
    // produced rather than the sum being taken.
    //
    // 5px of lift plus 1.1x about the icon centre put the 0.x "$"'s top (y=6.5) at
    // y=0.95, i.e. 0.45px inside the box once the 0.5px stroke is counted — the ceiling.
    // lucide-react v1 shortened the bar to y 7..17, so the same numbers now peak at
    // 12 - 5*1.1 - 5 = 1.5: a unit of headroom this recipe deliberately does not spend,
    // since the approved gesture was never limited by more than that ceiling.
    '@keyframes veraNavCharge': {
      '0%': { transform: 'translateY(0) scale(1)' },
      '45%': { transform: 'translateY(-5px) scale(1.1)' },
      '100%': { transform: 'translateY(0) scale(1)' },
    },

    // Roles & permissions — the check is stamped into the shield.
    //
    // STAMPED, NOT DRAWN, and the difference is deliberate. Certificates' check writes
    // itself along its own stroke, and this file's constraint 2 says that paint-bound
    // technique stays on exactly one icon. It also should: a certificate's check is a
    // mark being MADE (an act, taking time), where a permission check is a mark being
    // APPLIED (a decision, landing). So this one arrives from up and to the left at 1.35x
    // and settles onto the shield, which holds still throughout.
    //
    // IT NEVER GOES TO ZERO, and that is the fix rather than a preference. This opened at
    // `opacity: 0`, and was reported as flashing. The cause is the pairing of that zero
    // with `EASE`, whose P2.x is 0 and which therefore front-loads hard: solved, the curve
    // is at y=0.855 by x=0.31, so over the 231ms first segment the opacity travelled
    // 0 -> 0.86 in 72ms. Measured on the real element, 0.000 -> 0.859. A full-range
    // luminance swing inside a tenth of a second is not a fade, it is a blink — and what
    // blinks is the half of the glyph that carries its meaning, so the shield rendered
    // briefly alone with a hole in it. 1.55x compounded it: a CSS transform on an SVG
    // child scales the STROKE too, so the mark that popped in was also 55% heavier.
    //
    // THE FILE'S OWN PRECEDENT SAYS A FADE IS THE WRONG TOOL HERE. Three recipes start
    // displaced and two carry no opacity at all — `veraNavDrop`'s pin is "simply 4.5px
    // higher in the ROW for the opening frames", and `veraNavDawn`'s sun starts low and
    // small. The one that does fade, `veraNavArrive`, earns it semantically: a SECOND
    // figure walks in from behind the first, so it genuinely was not there. The check is
    // not that. It is a permanent part of the glyph at rest, so animating it out of
    // nothing misreads the icon as well as flashing.
    //
    // 0.55 rather than dropping the channel outright, because the travel here is
    // TRANSLATE-dominated — scaling about (12,12) barely moves a shape already centred
    // there, so the ~5px this needs to clear the amplitude floor has to come from the
    // translate, and the check therefore starts genuinely off the shield. `veraNavDrop`
    // can go bare because it is WHOLE-GLYPH: everything moves together and reads as the
    // icon sitting high. A CHILD appearing 5px off its parent at full strength reads as a
    // misregistered stamp. So the remaining 0.45 of swing is doing content work — ink not
    // yet set — instead of masking a displaced start.
    //
    // Peak travel 6.68px -> 6.03px (max bounding-box corner vs settled), still inside the
    // 4.5-7.5px band. The amplitude was never the complaint and is not what was cut.
    //
    // `both` fill costs nothing here (there is no delay) and keeps the final state pinned
    // rather than snapping back to an un-opacitied rest.
    '@keyframes veraNavStamp': {
      '0%': { opacity: 0.55, transform: 'translate(-2px, -4.5px) scale(1.35)' },
      '55%': { opacity: 1, transform: 'translate(0, 0) scale(1)' },
      '100%': { opacity: 1, transform: 'translate(0, 0) scale(1)' },
    },

    // Controls — each slider handle runs to the other end of its own track gap.
    //
    // 4px IS THE GLYPH'S OWN GEOMETRY, not a number picked to look right. lucide draws
    // each row as two track segments with a 4-unit gap, and the handle sits at one end of
    // that gap: row 1's gap is x 10..14 with the handle at 14, row 2's is 8..12 with the
    // handle at 8, row 3's is 12..16 with the handle at 16. Moving each handle 4px toward
    // the empty end lands it exactly on the far edge of its own gap and never crosses a
    // track stroke. A bigger number would put a handle on top of the track it slides
    // along, which is a slider that has come off its rail.
    '@keyframes veraNavSlideL': {
      '0%': { transform: 'translateX(0)' },
      '45%': { transform: 'translateX(-4px)' },
      '100%': { transform: 'translateX(0)' },
    },
    '@keyframes veraNavSlideR': {
      '0%': { transform: 'translateX(0)' },
      '45%': { transform: 'translateX(4px)' },
      '100%': { transform: 'translateX(0)' },
    },

    // My requests — the ticket tears off the roll, hinging on its left notch.
    //
    // The origin is the point, not the angle: pivoting at (2,12) puts the far bottom
    // corner 21.2 units out, so 12 degrees moves it 2 * 21.2 * sin(6) = 4.43px, where the
    // same 12 degrees about the icon's centre would have moved it half that. Whole-glyph,
    // so the viewBox does not bind (measured overflow 0.00px); 12 is chosen because a
    // ticket tearing off the roll hinges, it does not swing. Measured travel 4.76px.
    '@keyframes veraNavTear': {
      '0%': { transform: 'rotate(0deg)' },
      '40%': { transform: 'rotate(12deg)' },
      '72%': { transform: 'rotate(-4deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },

    // Features — the toggle throws.
    //
    // THE THROW IS THE GLYPH'S GEOMETRY AND NOT AN AMPLITUDE CHOICE: it is the distance
    // from the knob's "off" centre to its "on" centre, i.e. lucide's own `toggle-right`.
    // In lucide-react v1 the knob is r=3 at cx=9 in a track running x 2..22, and
    // `toggle-right` puts it at cx=15 — so the throw is exactly 6. (0.x drew a smaller
    // knob at cx=8 with its "on" at cx=16, and this was 8px; carrying 8 onto the v1 glyph
    // would carry the knob 2 units past its "on" position.)
    // Any other number lands the knob somewhere a toggle never rests, which reads as stuck
    // rather than switched. The track holds still — rotating this glyph is the `contrast`
    // trap again, since a stadium with a round knob has nothing rotation can express.
    '@keyframes veraNavToggle': {
      '0%': { transform: 'translateX(0)' },
      '45%': { transform: 'translateX(6px)' },
      '100%': { transform: 'translateX(0)' },
    },

    // Profile — the figure leans in, then back.
    //
    // ABOUT THE FEET, at (12,22), which is the origin with a usable radius: the head sits
    // 19 units above it, so 13 degrees moves the crown 19 * sin(13) = 4.27px, while the
    // same angle about the icon's centre would have moved it 1.1px. Measured peak travel
    // 5.11px (the bbox corner exceeds the crown's own displacement).
    //
    // Distinct from `veraNavRing` (Notifications), which is also a pendulum: that one
    // hangs from its crown and decays over four beats like a struck bell; this one
    // pivots on its base over two, like a person inclining toward you. Different pivot,
    // different rhythm, and — the part that matters — each is what its own object does.
    //
    // A lift was the obvious first idea and was rejected for the wrong reason (a viewBox
    // clip that a whole-glyph transform cannot suffer — see constraint 1). It stays
    // rejected on the right one: a figure that rises off the row says nothing about a
    // person, where inclining toward the reader does.
    '@keyframes veraNavLean': {
      '0%': { transform: 'rotate(0deg)' },
      '32%': { transform: 'rotate(-13deg)' },
      '66%': { transform: 'rotate(8deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },

    // Business figures — the keypad is worked and the total pops out.
    //
    // THREE KEYFRAMES, ONE GESTURE, and the ask is why the body still hops: "jump like it
    // does now, but also click buttons on calculator". The hop is what was already there
    // and is kept deliberately; what is ADDED is the keypad being used underneath it. The
    // shape to compare against is `veraNavPlugIn` + `veraNavProng` — element-specific
    // keyframes that only mean anything together — not three motions that happen to
    // overlap on one glyph.
    //
    // THE HOP IS CALIBRATED, NOT INVENTED, and the arithmetic is the reason "more
    // interesting" was the ask in the first place. What it replaces is the generic
    // `.lucide` fallback, `veraNavPop`: scale(1.1) about the glyph's own centre moves the
    // 24px box's corner 12 * 0.1 * sqrt2 = 1.70px, i.e. under a THIRD of this file's
    // 4.5px floor. It was never a calibrated recipe — the header calls the fallback a
    // floor for rows nobody has thought about yet, and this row had not been. Reaching
    // the band on SCALE alone would need ~1.27x, which is louder than `veraNavSwell`
    // (Certificates, 1.2) and would read as a zoom rather than a jump. A whole-glyph
    // TRANSLATE moves the box by exactly its own amplitude and cannot be clipped
    // (constraint 1), so 4.5px of lift measures 4.5px and the gesture stays a jump.
    //
    // NEIGHBOUR, NAMED HONESTLY RATHER THAN GLOSSED: `veraNavBob` (Packages' parcel,
    // Docs' book) is also a translateY hop, and in isolation this is the same channel at
    // a bigger number — a thinner distinction than most in this file, recorded the way
    // `veraNavHinge` records its own. Bob is a single soft 2px lift on an object being
    // HANDLED; this is 4.5px timed to land on the third keystroke, on a machine being
    // WORKED. What separates them is the keypad, not the hop. Neither half of `plug` is
    // distinctive alone either, and that is the precedent being leaned on — so if a
    // fourth translateY hop is ever wanted, the channel is full and the answer is a
    // different idea, not a fourth amplitude.
    '@keyframes veraNavReckon': {
      '0%': { transform: 'translateY(0)' },
      '40%': { transform: 'translateY(-4.5px)' },
      '100%': { transform: 'translateY(0)' },
    },
    // A round key is struck: it sinks, and its mark thickens under the finger.
    //
    // SCALE UP, NOT DOWN, AND THE ICON'S OWN STROKE IS WHY. Both navs render lucide at
    // `strokeWidth: 1`, and a key is `M8 18h.01` — a 0.01-long stroke with a round cap, so
    // the ENTIRE key is one stroke-width: a 1px dot. Pressing it "in" (the obvious first
    // idea: scale down, fade) would take a 1px mark to ~0.6px at half opacity, which at
    // this size is not a press but a disappearance, and an invisible press is not a press.
    // Doubling is the same physical idea read the other way — more ink where the finger
    // is — and it is the only channel with room here. Transforms scale the STROKE too (no
    // `vector-effect` anywhere), which is what makes a zero-area path scalable at all.
    //
    // 2x CLEARS ITS NEIGHBOURS: lucide spaces the keys 4 units apart, so a 1px dot
    // becoming 2px still leaves 2 units between it and the key beside it. The 1.1px sink
    // is a shade over the dot's own resting diameter, which is what makes the movement
    // read as a travel rather than a jitter.
    '@keyframes veraNavKeyTap': {
      '0%': { transform: 'translateY(0) scale(1)' },
      '45%': { transform: 'translateY(1.1px) scale(2)' },
      '100%': { transform: 'translateY(0) scale(1)' },
    },
    // The TALL key — lucide's (16,14)-(16,18) bar, the equals of the pad — takes the same
    // strike on a DIFFERENT AXIS, which is the whole reason it cannot share the keyframe
    // above rather than a preference for two.
    //
    // It is 4 units LONG where the round keys are 1px wide, so the uniform scale that
    // doubles a dot would add 4 units to this bar's length: a key that grows is
    // stretching, not being pressed. scaleX thickens the stroke and leaves the length
    // exactly alone, so the bar bolds in place. 2.4 rather than 2.0 because a bar carries
    // its weight along its length — the same proportional thickening reads quieter on it
    // than on a dot. At peak its right edge sits at 17.2, still 2.8 units inside the
    // body's inner wall at x=20.
    '@keyframes veraNavBarTap': {
      '0%': { transform: 'translateY(0) scaleX(1)' },
      '45%': { transform: 'translateY(1.1px) scaleX(2.4)' },
      '100%': { transform: 'translateY(0) scaleX(1)' },
    },

    // ── Account chip menu keyframes ──────────────────────────────────────────────
    //
    // The account menu's three rows render `user`, `contrast` and `log-out`, plus the
    // `chevron-right` disclosure mark on the Theme row. The first two already had
    // recipes — the SAME glyphs for the SAME concepts on the Settings nav (Personal ->
    // Profile leans, Appearance wipes), so they carry over exactly as `blocks`, `users`
    // and `life-buoy` did between the rail and Settings. The two below are new.

    // Log out — the arrow leaves, and shortens as it goes.
    //
    // TRANSLATION ALONE CANNOT REACH THE BAND HERE, and the ceiling is a hard one: the
    // arrowhead's tip sits at x=21 with a 0.5 half-stroke, so a CHILD transform has
    // exactly 2.5px before the tip is cut by the svg's own overflow. Every 4.5px+
    // translate of this arrow clips, and clipping the POINT of an arrow is the one part
    // that carries its meaning.
    //
    // So the amplitude comes from a second channel with room in it: the arrow also
    // compresses toward its own head. Pivoting on the tip (21,12), scaleX(0.76) pulls
    // the TAIL 2.88 units forward before the translate is applied, so the tail travels
    // 2.88 + 2.4 = 5.28px while the tip travels only the 2.4 it can afford. Measured
    // peak travel 5.31px, 0.00px clipped (tip lands at 23.4 + 0.5 = 23.9 of 24).
    //
    // It is also the right STORY rather than a workaround: a thing passing out through
    // an exit has its trailing end catch up to its leading end. An arrow that merely
    // slid right would be `veraNavSlideR` (a slider handle) at a different amplitude —
    // no two unrelated icons share a motion in this file, and "the handle moved" says
    // nothing about leaving.
    //
    // The room the arrow leaves — lucide's third `path` — holds still throughout. It is
    // the thing you are departing FROM; moving it would make the whole glyph slide,
    // which is the generic motion this file exists to avoid.
    '@keyframes veraNavDepart': {
      '0%': { transform: 'translateX(0) scaleX(1)' },
      '45%': { transform: 'translateX(2.4px) scaleX(0.76)' },
      '100%': { transform: 'translateX(0) scaleX(1)' },
    },

    // Theme — the disclosure chevron draws back, then points on.
    //
    // Whole-glyph, so the viewBox does not bind at any amplitude (constraint 1) and the
    // number is chosen by how a 24px mark reads at the right edge of a 220px menu.
    //
    // THE ANTICIPATION IS WHAT MAKES IT NOT A SLIDER. A flat out-and-back translate is
    // `veraNavSlideR`, already spoken for by a Controls slider handle, and the two would
    // be the same gesture on unrelated glyphs. Drawing back first and then throwing
    // forward is a different rhythm and a different sentence: a slider handle is moved BY
    // you, a disclosure chevron beckons you on.
    //
    // Measured peak travel 5.00px — the forward throw, which is the larger of the two
    // excursions from rest (the pull-back contributes 1.6px and is not additive under the
    // max-from-rest convention this file measures by; an earlier draft claimed the 6.6px
    // sweep and was wrong about which number the band refers to). It has room: the glyph
    // box sits 8px inside the row and its ink ~25px inside the paper, so a 5px throw
    // still lands ~20px clear of the menu's edge.
    //
    // The alternative considered was silence — the chevron is a disclosure mark, not the
    // row's subject, and the Collapse row's precedent is that chrome operating chrome
    // holds still. It lost because this chevron does not operate the menu, it NAMES the
    // destination: the row opens a submenu to the right, which is exactly what a
    // rightward nudge says. Leaving it on the generic `pop` was never an option — a
    // disclosure mark that swells 10% says nothing at all.
    '@keyframes veraNavBeckon': {
      '0%': { transform: 'translateX(0)' },
      '25%': { transform: 'translateX(-1.6px)' },
      '60%': { transform: 'translateX(5px)' },
      '100%': { transform: 'translateX(0)' },
    },

    // ── Theme submenu keyframes ──────────────────────────────────────────────────
    //
    // The Theme row's submenu renders `sun` / `moon` / `laptop` (Light / Dark / System)
    // plus a `check` on the active one. Until now that paper carried NO motion at all —
    // `accountMenuIconMotionSx` is scoped to the parent menu's own Paper and the submenu
    // spreads a different style object (see the ⚠️ at the foot of this file) — so hovering
    // out of a menu whose icons move into one whose icons sit dead was the visible seam
    // this file's "every icon moves" floor exists to prevent. The three below close it.
    //
    // All three are WHOLE-GLYPH, so nothing can be clipped by the svg's own overflow at
    // any amplitude (constraint 1) and none of them needs an entry in
    // SUB_TARGETED_ICON_SIGNATURES — a lucide reorder cannot retarget a recipe that never
    // names a child.

    // Light — the sun comes up.
    //
    // IT STARTS DISPLACED, like `veraNavDrop` and `veraNavArrive`: the disc sits below its
    // rest position and small at t=0, then rises and swells past 1 before settling. That is
    // the whole point — a sun does not jump up and come back, it ARRIVES.
    //
    // Rotation was the obvious first idea and is the `badge-check` trap verbatim: lucide
    // draws eight evenly-spaced rays, so the glyph maps onto itself every 45 degrees and
    // ~22 is the most rotation the shape can express — which at the rays' ~10px radius buys
    // 3.9px, under this file's 4.5px floor, for a gesture that would also have been a
    // fourth `veraNavTurn`. Rise has no period and no neighbour.
    '@keyframes veraNavDawn': {
      '0%': { transform: 'translateY(4px) scale(0.82)' },
      '60%': { transform: 'translateY(0) scale(1.06)' },
      '100%': { transform: 'translateY(0) scale(1)' },
    },

    // Dark — the crescent rocks on its own arc.
    //
    // THE PIVOT IS THE WHOLE RECIPE, and it is what separates this from `veraNavLean`
    // (Profile), which is also a two-beat rotation and is on screen at the same time one
    // row up. Lean pivots on a standing figure's FEET, so the glyph tips about its base
    // like a person inclining toward you. This pivots on the crescent's outer arc at its
    // LEFT (4,12) — the point a crescent physically rests on — so the shape rocks
    // sideways in a cradle rather than nodding forward. Different axis of motion for
    // different objects, which is the rule this file states as "no two unrelated icons
    // may share a motion".
    //
    // The side pivot is also what supplies the amplitude: the crescent's far corner sits
    // ~19 units from (4,12), so 15 degrees moves it 2 * 19 * sin(7.5) = 4.96px, where the
    // same angle about the icon's own centre would have moved it roughly half that.
    '@keyframes veraNavCradle': {
      '0%': { transform: 'rotate(0deg)' },
      '35%': { transform: 'rotate(-15deg)' },
      '70%': { transform: 'rotate(6deg)' },
      '100%': { transform: 'rotate(0deg)' },
    },

    // System — the lid hinges open.
    //
    // scaleY about the laptop's own base, so the screen swings up off the keyboard the way
    // a lid does.
    //
    // 0.66, NOT THE 0.5 THIS SHIPPED AT FIRST, and the correction came from a measurement
    // rather than taste. The arithmetic estimate said 0.5 -> 1 would move the top edge
    // ~7.5px; the browser said 8.33px, i.e. over the 4.5-7.5px band and the loudest thing
    // in either nav. (The estimate was wrong because it reasoned in 24-unit viewBox space
    // while this glyph renders at 20 — the same size assumption the origin note below is
    // about.) Travel is linear in (1 - startScale) here: 0.5 -> 8.33px measures out at
    // 16.66px of glyph height, so 0.66 -> 5.66px, mid-band. Re-measure if the number moves.
    //
    // NEIGHBOUR, NAMED HONESTLY: `veraNavErect` (Settings -> Company -> General) is also a
    // scaleY from a base. The two are never co-visible — one is a Settings nav row, this is
    // a submenu three surfaces away — and the rhythms differ (a building GROWS smoothly to
    // a 3% overshoot; a lid is THROWN open, overshoots 10% and rebounds under before
    // settling). That is a thinner distinction than most in this file and it is recorded
    // rather than glossed: if a third scaleY-from-base recipe is ever wanted, the channel
    // is full and the answer is a different idea, not a fourth set of numbers.
    '@keyframes veraNavHinge': {
      '0%': { transform: 'scaleY(0.66)' },
      '55%': { transform: 'scaleY(1.12)' },
      '78%': { transform: 'scaleY(0.97)' },
      '100%': { transform: 'scaleY(1)' },
    },

    // ── Fallback: every lucide glyph in the nav ──────────────────────────────────
    ...on('.lucide', { animation: `veraNavPop 340ms ${EASE}` }),

    // ── Whole-glyph recipes (no dependency on anything inside the icon) ──────────

    // Labs — a conical flask tips to pour, pivoting on its base.
    ...on(icon('flask-conical'), {
      animation: `veraNavTilt 420ms ${EASE}`,
      ...viewBox('12px 21px'),
    }),

    // Settings / Demos / Subscriptions — the three rows whose object genuinely rotates:
    // a gear budging, a radar sweeping, a repeat loop advancing. This is the ONE shared
    // recipe left, and it is shared because "this thing turns" is the actual meaning in
    // all three cases, not because they ran out of ideas. Help used to be here and was
    // the odd one out — a life ring floats, it does not rotate on a bearing.
    ...on(icon('settings'), { animation: `veraNavTurn 440ms ${EASE}` }),
    ...on(icon('radar'), { animation: `veraNavTurn 440ms ${EASE}` }),
    ...on(icon('repeat'), { animation: `veraNavTurn 440ms ${EASE}` }),

    // Support — the internal rail's Support row, the Help popover's own menu row, and
    // Settings -> Company -> Support. Same glyph, same object, so one recipe: the ring
    // lifts on a swell and rolls a little.
    ...on(icon('life-buoy'), { animation: `veraNavBuoy 620ms ${EASE_DELIBERATE}` }),

    // Search — the glass sweeps across, pivoting on the lens rather than the icon box so
    // the tilt looks like the handle moving and not the whole thing shearing.
    ...on(icon('search'), {
      animation: `veraNavScan 480ms ${EASE_DELIBERATE}`,
      ...viewBox('11px 11px'),
    }),

    // Products / Orders — the bag takes a load. Pivoting on the rim (12,3) so the squash
    // hangs downward off the handles instead of collapsing toward the middle.
    ...on(icon('shopping-bag'), {
      animation: `veraNavLoad 460ms ${EASE}`,
      ...viewBox('12px 3px'),
    }),

    // Sales — the coins jump in the hand. The two <circle>s ARE the coins (the
    // hand is three <path>s), so `nth-of-type` counts only circles and a future
    // decorative path cannot shift them.
    //
    // `transform-box: view-box` is required on a CHILD for a px origin to resolve
    // in the icon's 24x24 space; here the origin is the default centre, so only
    // the box matters.
    ...on(`${icon('hand-coins')} circle:nth-of-type(1)`, {
      animation: `veraNavCoin 420ms ${EASE}`,
      transformBox: 'view-box',
    }),
    ...on(`${icon('hand-coins')} circle:nth-of-type(2)`, {
      // A beat behind, so the pair reads as two coins and not one bar.
      animation: `veraNavCoin 420ms ${EASE} 70ms`,
      transformBox: 'view-box',
    }),

    // Packages — a parcel gets a short hop.
    ...on(icon('box'), { animation: `veraNavBob 380ms ${EASE}` }),

    // Docs — the open book lifts off the shelf.
    ...on(icon('book-open'), { animation: `veraNavBob 380ms ${EASE}` }),

    // Companies — a shopfront grows from its footing, not from its middle.
    ...on(icon('store'), {
      animation: `veraNavRise 380ms ${EASE}`,
      ...viewBox('12px 21px'),
    }),

    // What's new — the megaphone kicks back from its narrow end, the way one does
    // when someone shouts through it. The narrow end is x=3, y 6..14 in lucide-react v1
    // (it was y 11..14 in 0.x, hence the old 12.5), so its middle is (3,10).
    ...on(icon('megaphone'), {
      animation: `veraNavShout 400ms ${EASE}`,
      ...viewBox('3px 10px'),
    }),

    // Tags — the tag turns over.
    //
    // THIS IS A DIFFERENT IDEA, NOT A RETUNE. The first two attempts were a two-beat
    // pendulum and then a single settle, both of them the tag PIVOTING ABOUT ITS HOLE.
    // That is the obvious reading of the shape, and reading it twice more slowly did not
    // make it better — it just produced another Z-rotation in a rail that already had
    // five. The hole is where a tag hangs FROM; it says nothing about what a tag IS.
    //
    // A tag has two sides, and the thing you actually do with one is turn it over to read
    // the back. So this rotates about Y instead: the glyph turns edge-on and comes back.
    // It is the only Y-axis motion in the file, which is what finally stops Tags reading
    // as a cousin of the bag, the flask and the gear — a different AXIS reads as a
    // different kind of object, where a different angle on the same axis does not.
    //
    // Stopped at 48 degrees rather than carried through 180: a full flip would land on
    // the glyph's mirror image, which for a stroke drawing is just a tag pointing the
    // wrong way, and 180 is not identity so it would need a second half to unwind.
    //
    // Default origin (the icon's own centre) is correct here and is deliberately not
    // overridden — you turn a tag about its middle, not about the corner it hangs by.
    ...on(icon('tag'), { animation: `veraNavFlip 520ms ${EASE_DELIBERATE}` }),

    // Settings -> Company -> General — the building goes up from its footing.
    //
    // `building-2` — AND UNDER 0.x IT HAD TO BE `building2`, which is the trap worth
    // knowing. 0.x stamped `lucide-${toKebabCase(ComponentName)}`, and that toKebabCase
    // inserts a hyphen only before an UPPERCASE letter, so `Building2` rendered
    // `lucide-building2` while its file was `building-2.js`. lucide-react v1 stamps the
    // icon's real name plus its aliases instead: `Building2Icon` now renders
    // `building-complex`, with `lucide-building-2` as an alias class — and no
    // `lucide-building2` at all. So the 0.x selector silently stopped matching on the bump.
    //
    // A selector that matches nothing fails SILENTLY — the row simply falls through to the
    // `pop` fallback and looks animated, which is why the 0.x version survived a
    // read-through and was caught only by enumerating the rendered classes. The guard now
    // checks every class this file names against the classes lucide emits (v1 records the
    // aliases, which 0.x did not), so this one is a build failure rather than a hunt.
    //
    // Origin (12,21): v1's building stands on y=21, not 0.x's y=22.
    ...on(icon('building-2'), {
      animation: `veraNavErect 400ms ${EASE}`,
      ...viewBox('12px 21px'),
    }),

    // Settings -> Personal -> Notifications — the bell rings, hanging from its crown.
    ...on(icon('bell'), {
      animation: `veraNavRing 620ms ${EASE_DELIBERATE}`,
      ...viewBox('12px 3px'),
    }),

    // Settings -> Company -> Address book — the pin drops and plants on its point.
    ...on(icon('map-pin'), {
      animation: `veraNavDrop 480ms ${EASE}`,
      ...viewBox('12px 22px'),
    }),

    // Settings -> Personal -> My requests — the ticket tears off, hinging on its notch.
    ...on(icon('ticket'), {
      animation: `veraNavTear 480ms ${EASE_DELIBERATE}`,
      ...viewBox('2px 12px'),
    }),

    // The account menu's Theme row — its disclosure chevron beckons toward the submenu.
    //
    // SAFE TO PUT IN THE SHARED TABLE, and that was checked rather than assumed: the only
    // `ChevronRight` inside any of the three scoped containers is this one row's. The
    // rail's disclosure group (`SidebarNestedList`) uses `ChevronDown`/`ChevronUp`, and
    // the Settings nav renders no chevron at all. So this recipe reaches exactly one
    // glyph today, and a second chevron-right added to a nav later would inherit a
    // gesture that already means what that chevron would mean.
    ...on(icon('chevron-right'), { animation: `veraNavBeckon 440ms ${EASE_DELIBERATE}` }),

    // The Theme submenu's three rows. See the keyframes above for why each is the gesture
    // it is; the origins here are what give the last two their amplitude.
    //
    // SCOPE CHECKED, NOT ASSUMED, the same way `chevron-right` was. `sun`, `moon`, `laptop`
    // and `check` are rendered in exactly ONE place inside any of the four scoped
    // containers — this submenu. The Settings nav deliberately avoids sun/moon for its
    // Appearance row (there is a comment in SettingsPage saying so, because the control's
    // own options are Sun/Moon/Laptop and the nav icon would have repeated them), and
    // CompanyGeneralPage's Sun/Moon sit in the PAGE, which no motion container reaches —
    // `settingsNavIconMotionSx` is applied to the nav column, not the page.
    //
    // ⚠️ THESE TWO ORIGINS ARE PERCENTAGES, AND EVERY OTHER ORIGIN IN THIS FILE IS PX.
    // That is not an inconsistency, it is the one place the px convention does not hold.
    // The `viewBox()` helper's own note says px works "because lucide renders at its
    // default size 24 on a 0 0 24 24 viewBox, so the element box and the coordinate space
    // are the same 24x24" — and this submenu renders its glyphs at `size={20}`, the only
    // nav surface that does. For the OUTERMOST svg, `transform-box: view-box` resolves
    // against the SVG VIEWPORT, i.e. the 20x20 CSS box, so `4px` would mean 4/20 across
    // rather than the intended 4/24. Measured before the fix: computed origin came back
    // `10px 10px` on a 20px box.
    //
    // Percentages carry the intent at any size: 4/24 = 16.67%, 20/24 = 83.33%. Use px
    // only where the glyph is known to render at 24.
    ...on(icon('sun'), { animation: `veraNavDawn 440ms ${EASE_DELIBERATE}` }),
    ...on(icon('moon'), {
      animation: `veraNavCradle 480ms ${EASE_DELIBERATE}`,
      // The crescent's outer arc at its left — the point it would rest on. See the keyframe.
      ...viewBox('16.67% 50%'),
    }),
    ...on(icon('laptop'), {
      animation: `veraNavHinge 460ms ${EASE_DELIBERATE}`,
      // The base the lid swings up from.
      ...viewBox('50% 83.33%'),
    }),

    // The check on the ACTIVE theme holds still, and this is the one deliberate hole in
    // the "every icon moves" floor at the top of this file.
    //
    // It is the selected-row argument (inert rule 1) applied to a glyph instead of a row.
    // The check is not the row's subject, it is the answer to "which theme am I on" — and
    // that answer must not depend on where the pointer happens to be. The row it sits on
    // already has a moving subject (the sun, moon or laptop beside it), so animating the
    // check as well would put two independent gestures on one row while its two siblings
    // have one each.
    //
    // Without this rule the `.lucide` fallback would give it `veraNavPop`, i.e. the generic
    // 10% swell this file describes as "motion without information". Silence is the correct
    // reading here, not a missing recipe.
    ...on(icon('check'), { animation: 'none' }),

    // ── Sub-element recipes (pinned by SUB_TARGETED_ICON_SIGNATURES + the guard) ──

    /*
      Analytics — "the bars go up and down" (the ask, verbatim).

      lucide-react v1 draws the three bars as `path`s, LEFT TO RIGHT (x=5, x=12, x=19),
      so the delays run 1,2,3 and the wave reads left-to-right, the direction a chart is
      scanned. (0.x drew them as `line`s, right to left, and the delays ran 3,2,1 — the
      bump flipped both, which is why these selectors changed.)

      scaleY alone, about the shared baseline at y=21 (v1; it was 20) — and because it is
      scaleY only, the x half of the origin is irrelevant, which is why one origin serves
      all three bars without naming any bar's own x.
    */
    ...on(icon('chart-no-axes-column'), { animation: 'none' }),
    ...on(`${icon('chart-no-axes-column')} path`, viewBox('0 21px')),
    ...on(`${icon('chart-no-axes-column')} path:nth-of-type(1)`, {
      animation: `veraNavBar 400ms ${EASE} 0ms`,
    }),
    ...on(`${icon('chart-no-axes-column')} path:nth-of-type(2)`, {
      animation: `veraNavBar 400ms ${EASE} 55ms`,
    }),
    ...on(`${icon('chart-no-axes-column')} path:nth-of-type(3)`, {
      animation: `veraNavBar 400ms ${EASE} 110ms`,
    }),

    /*
      Certificates — "badge spins and checkmark marks" (the ask, verbatim).

      ONE GESTURE, NOT TWO ANIMATIONS THAT HAPPEN TOGETHER. The badge turns for 860ms;
      the check starts writing at 240ms and finishes at 860ms — the same instant the badge
      comes to rest. So the turn leads, the check is written across the settle, and the
      two land together. Firing both flat at t=0 (the first version) read as two unrelated
      twitches, and the turn on its own was over before the eye arrived.

      THE CHECK IS DRAWN, NOT STAMPED. path 2 is the check (CHECK_PATH_D); dashing it to
      its own length and walking the offset to zero reveals the stroke progressively from
      the short arm's tip, through the elbow, out to the long arm — the order a person
      writes it in. In 0.x that was the path's own direction and came for free; v1 drew
      the path the other way, so `veraNavDraw` now walks a NEGATIVE offset to reveal it
      from the end instead (see CHECK_PATH_D). The previous version scaled the whole
      glyph up from 0.55 with opacity, which is a stamp landing, not a mark being made. (`shield-check` gets that stamp,
      deliberately, because a permission check IS a decision landing rather than a mark
      being made — see its recipe.)

      `both` fill matters: the check is held undrawn through its 240ms delay rather than
      sitting complete and then blinking out at the moment the animation starts.

      `stroke-dasharray` is set here, not globally, so at rest the check has no dash
      pattern at all and cannot be caught mid-draw by anything else.

      This is the one paint-bound animation in the file. See constraint 2 in the header
      for why that is acceptable here and must not be copied to a second icon.
    */
    ...on(icon('badge-check'), { animation: `veraNavSwell 860ms ${EASE_DELIBERATE}` }),
    ...on(`${icon('badge-check')} path:nth-of-type(1)`, {
      animation: `veraNavBadgeTurn 860ms ${EASE_DELIBERATE}`,
      ...viewBox('12px 12px'),
    }),
    ...on(`${icon('badge-check')} path:nth-of-type(2)`, {
      animation: `veraNavDraw 620ms ${EASE_DRAW} 240ms both`,
      strokeDasharray: CHECK_PATH_LENGTH,
    }),

    /*
      Studio — the colours are sampled in turn.

      READ THE HISTORY BEFORE CHANGING THIS AGAIN; it has been round the houses. Attempt
      one pulsed the four wells in turn. Attempt two had them arrive one by one from
      nothing. Attempt three — the version this replaces — dropped the sequence entirely
      for a FIGURE/GROUND move: one well grew and lifted while the other three faded to
      0.1 opacity, on the argument that a stagger in which all four wells do the same
      thing says only "these dots exist", where picking one says what Studio is FOR.

      The argument was good and the result was not. Fading three of four wells by 90% at
      the same instant is, to the eye, the whole field of dots blinking — so the thing
      that read as moving was the PALETTE, not any well in it, which is the opposite of
      what a figure/ground move is supposed to do. Reported verbatim as "its weird that
      it pulsing the pallet — it shoud pulse multiple". Note that the complaint is not
      about amplitude: the recipe measured 4.08px, under this file's own 4.5px floor, so
      it was simultaneously too quiet AS A GESTURE and too loud AS A FIELD.

      So the sequence comes back, deliberately, against the previous version's reasoning
      — and the way it earns a subject is ORDER. The wells fire around the palette's rim,
      lower-left -> upper-left -> top -> right, each rising and swelling as its turn
      comes. That is a brush running across the colours before one is chosen, which is a
      real act on a palette and is not available to a simultaneous move. Attempt one's
      defect was that its order was lucide's ARRAY order, which is spatially arbitrary and
      therefore looked like four unrelated twitches.

      THE RECEDE IS GONE ON PURPOSE. Nothing dims. It was the only part of the old recipe
      that touched all four wells at once, and it was the part the user saw.

      Ordering, in lucide-react v1's source order, is (13.5,6.5), (17.5,10.5), (6.5,12.5),
      (8.5,7.5) — so the rim order is nth 3, 4, 1, 2 and the delays below look shuffled.
      They are not; they are sorted by POSITION, and the comment on each says which well
      it is. A lucide reorder permutes which well leads rather than breaking the recipe —
      v1 did exactly that (it swapped the last two wells and moved the outline first), so
      the run would have started upper-left and doubled back; it was re-sorted here.

      70ms between wells, 360ms each, 570ms in all. Analytics' bars are the other stagger
      in this file and sit at 55/400 — close enough to be the same family, which is right
      (both are "a run across a set of like parts") and does NOT breach the no-shared-
      motion rule, because the shared thing is the rhythm and the keyframes are unrelated:
      a bar dips in place, a well rises off the surface.

      `fill-box` so each well pivots on its own centre; lucide's four circles sit at four
      unrelated coordinates, and hardcoding them here would be four more numbers to drift.
    */
    ...on(icon('palette'), { animation: 'none' }),
    ...on(`${icon('palette')} circle`, fillBox('center')),
    // (6.5,12.5) — the lower-left well, where the run starts.
    ...on(`${icon('palette')} circle:nth-of-type(3)`, {
      animation: `veraNavSample 360ms ${EASE_DELIBERATE} 0ms`,
    }),
    // (8.5,7.5) — upper left.
    ...on(`${icon('palette')} circle:nth-of-type(4)`, {
      animation: `veraNavSample 360ms ${EASE_DELIBERATE} 70ms`,
    }),
    // (13.5,6.5) — the top of the rim.
    ...on(`${icon('palette')} circle:nth-of-type(1)`, {
      animation: `veraNavSample 360ms ${EASE_DELIBERATE} 140ms`,
    }),
    // (17.5,10.5) — the right-hand well, where it ends.
    ...on(`${icon('palette')} circle:nth-of-type(2)`, {
      animation: `veraNavSample 360ms ${EASE_DELIBERATE} 210ms`,
    }),

    /*
      People / Users — the second figure arrives.

      lucide-react v1's order is [front body, back head, back body, front head (circle)],
      so the back figure is paths 2 and 3 of 3 (`nth-of-type(n + 2)`) and the front pair is
      left untouched. (0.x had the circle second and the back pair the other way round —
      the same two paths, so this selector survived the bump; the pin is what proved it.)
      The back figure slides in from the right and fades up, held out of frame through an
      80ms delay so the front figure reads as already there.

      Both halves of the back figure share one rule, so they move as one person rather
      than a head chasing a body.
    */
    ...on(icon('users'), { animation: 'none' }),
    ...on(`${icon('users')} path:nth-of-type(n + 2)`, {
      animation: `veraNavArrive 400ms ${EASE} 80ms both`,
      ...viewBox('12px 12px'),
    }),

    /*
      Help — the question mark asks.

      WORTH KNOWING BEFORE YOU EDIT THIS: the sidebar's Help row uses `CircleHelp`, NOT
      `LifeBuoy`. LifeBuoy is the INTERNAL nav's Support row, the Help popover's own menu
      row, and Settings -> Company -> Support. The first pass at this section put a buoy
      recipe on `life-buoy` believing it was the Help row, and left Help itself on the
      generic fallback — the exact complaint it was meant to answer. Caught by measuring
      the rendered rail rather than by reading the menu arrays.

      The two paths are the hook and the dot — together, the "?" — and they tilt as one
      unit about the ring's centre while the `circle` holds still. Both paths are selected
      by a bare `path`, so no positional index is involved; the pin below is only for the
      "there are exactly two paths and the circle is not one of them" assumption.
    */
    ...on(icon('circle-help'), { animation: 'none' }),
    ...on(`${icon('circle-help')} path`, {
      animation: `veraNavAsk 460ms ${EASE_DELIBERATE}`,
      // The DOT, at (12,17) — see `M12 17h.01`. The "?" now pivots on its own point the
      // way a head tilts on a neck: the dot barely moves, and the hook nine units above it
      // sweeps a real arc. Pivoting on the icon's centre instead put the rotation's origin
      // inside the very glyph it was rotating, which is why 11 degrees bought 1px.
      ...viewBox('12px 17px'),
    }),

    /*
      Integrations — the detached block clicks into the tray.

      The most robust selector in this file: `rect` is the only rect in the glyph, so this
      survives lucide reordering, adding or removing paths entirely — v1 moved it from
      first to last and it held. What v1 DID change is the rect itself (8x8 at (14,2), was
      7x7 at (14,3)), which is why the origin below is its new centre and why `veraNavSnap`
      had to shrink.
    */
    ...on(icon('blocks'), { animation: 'none' }),
    ...on(`${icon('blocks')} rect`, {
      animation: `veraNavSnap 380ms ${EASE}`,
      ...viewBox('18px 6px'),
    }),

    /*
      Invoices — the three text rules print in from the left, top down.

      lucide-react v1's order is [rule y=16, rule y=8, rule y=12, torn-edge outline]: the
      outline moved from FIRST to LAST and the rules are no longer top-down. So the rules
      are paths 1-3, the outline (path 4) is left alone, and the delays go to 2, 3, 1 to
      keep the print top-down. (The 0.x selectors, `n + 2` and 2/3/4, would have printed
      the OUTLINE in from the left — the exact failure `veraNavPrint`'s 0.55 floor exists
      to soften.) Each rule is anchored at x=8; `fill-box` + `left center` pivots each on
      its own left edge, so the effect needs none of those coordinates written down.
    */
    ...on(icon('receipt-text'), { animation: 'none' }),
    ...on(`${icon('receipt-text')} path:nth-of-type(-n + 3)`, fillBox('left center')),
    // y=8 — the top rule.
    ...on(`${icon('receipt-text')} path:nth-of-type(2)`, {
      animation: `veraNavPrint 340ms ${EASE} 0ms`,
    }),
    // y=12 — the middle rule.
    ...on(`${icon('receipt-text')} path:nth-of-type(3)`, {
      animation: `veraNavPrint 340ms ${EASE} 60ms`,
    }),
    // y=16 — the bottom rule.
    ...on(`${icon('receipt-text')} path:nth-of-type(1)`, {
      animation: `veraNavPrint 340ms ${EASE} 120ms`,
    }),

    /*
      Fields — the two arrows reach out, top first, then bottom.

      lucide's order is [left arrowhead, top bar, right arrowhead, bottom bar], so the two
      ROWS are paths 1-2 and paths 3-4. Each row scales about the end it points AWAY from
      — the top row about (20,7), the bottom about (4,17) — so the arrow extends toward
      the side it indicates.

      The 90ms stagger is what makes it an exchange rather than a stretch: one direction
      goes, then the other answers. `both` fill so the delayed row is held compressed
      through its delay instead of sitting at rest and then jumping.
    */
    ...on(icon('arrow-left-right'), { animation: 'none' }),
    ...on(`${icon('arrow-left-right')} path:nth-of-type(-n + 2)`, {
      animation: `veraNavReach 420ms ${EASE_DELIBERATE} 0ms both`,
      ...viewBox('20px 7px'),
    }),
    ...on(`${icon('arrow-left-right')} path:nth-of-type(n + 3)`, {
      animation: `veraNavReach 420ms ${EASE_DELIBERATE} 90ms both`,
      ...viewBox('4px 17px'),
    }),

    /*
      Appearance — the dark half wipes across the disc while the ring holds.

      `circle` is untouched and `path` is the half-disc. Both are selected by element type
      rather than position, so a lucide reorder cannot swap them; the pin covers the
      "there is exactly one of each" assumption.
    */
    ...on(icon('contrast'), { animation: 'none' }),
    ...on(`${icon('contrast')} path`, {
      animation: `veraNavShade 460ms ${EASE_DELIBERATE}`,
      // scaleX only, so the y half of the origin does not matter; x=12 is the disc's
      // vertical diameter, which is the edge the shading grows out from.
      ...viewBox('12px 12px'),
    }),

    /*
      Referrals — the lid lifts off the box.

      lucide-react v1's order is [spine, box body, bow, lid rect] (0.x put the lid first;
      the bow is still the third PATH, so the selector held). The lid and the bow move as
      one (the bow is tied to the lid, so leaving it behind would tear the two apart), and
      the spine and body hold, so the gift visibly opens rather than jumping. The origin
      is the lid's centre, (12,9) in v1 — the lid group sits one unit higher than in 0.x;
      see `veraNavUnwrap`.
    */
    ...on(icon('gift'), { animation: 'none' }),
    ...on(`${icon('gift')} rect, ${icon('gift')} path:nth-of-type(3)`, {
      animation: `veraNavUnwrap 440ms ${EASE}`,
      ...viewBox('12px 9px'),
    }),

    /*
      Activity log — the clock hands run back.

      lucide's order is [arc, arrowhead, hands]. Only the hands move; the arc and its
      arrowhead already say "back" statically, and rotating them would turn the whole
      glyph into another gear. (In lucide-react v1 `History` renders `rotate-ccw-clock`,
      with `lucide-history` as an alias class; same three paths.)
    */
    ...on(icon('history'), { animation: 'none' }),
    ...on(`${icon('history')} path:nth-of-type(3)`, {
      animation: `veraNavRewind 520ms ${EASE_DELIBERATE}`,
      // The hands' own pivot, which is the dial's centre.
      ...viewBox('12px 12px'),
    }),

    /*
      Installation — the plug seats into its socket.

      lucide-react v1's order is [cord, right prong, body, left prong] — the prongs are
      paths 2 and 4. (0.x was [cord, left prong, right prong, body], i.e. 2 and 3; kept
      as-is, the v1 glyph would have squashed its BODY to a quarter of its height on every
      hover.) The SVG carries the lift and the two prongs carry the compression, which is
      why the whole-glyph rule below is an animation rather than the usual
      `animation: 'none'` suppression — here the glyph itself is one of the two moving
      parts.
    */
    ...on(icon('plug'), { animation: `veraNavPlugIn 460ms ${EASE_DELIBERATE}` }),
    ...on(
      `${icon('plug')} path:nth-of-type(2), ${icon('plug')} path:nth-of-type(4)`,
      {
        animation: `veraNavProng 460ms ${EASE_DELIBERATE}`,
        // scaleY about the prongs' shared base at y=8. scaleY only, so one origin serves
        // both prongs without naming either one's x.
        ...viewBox('0 8px'),
      },
    ),

    /*
      Billing — the "$" lifts off the receipt.

      lucide-react v1's order is [the vertical bar, the S curve, torn outline] — the
      outline moved from first to last, so the "$" is now paths 1 and 2 (it was 2 and 3,
      which on v1 would have lifted the S curve and the whole OUTLINE). The two halves of
      the "$" move as one; the outline holds.
    */
    ...on(icon('receipt'), { animation: 'none' }),
    ...on(
      `${icon('receipt')} path:nth-of-type(1), ${icon('receipt')} path:nth-of-type(2)`,
      {
        animation: `veraNavCharge 480ms ${EASE_DELIBERATE}`,
        ...viewBox('12px 12px'),
      },
    ),

    /*
      Roles & permissions — the check stamps into the shield.

      lucide's order is [shield, check]. The shield holds throughout, which is the point:
      the guard is already there and what changes is the decision landing on it.
    */
    ...on(icon('shield-check'), { animation: 'none' }),
    ...on(`${icon('shield-check')} path:nth-of-type(2)`, {
      animation: `veraNavStamp 420ms ${EASE} 0ms both`,
      ...viewBox('12px 12px'),
    }),

    /*
      Controls — the three handles run across their tracks, top to bottom.

      lucide-react v1 draws all nine parts as `path`s, sorted by their path data rather
      than grouped: the handles are paths 3 (row 1, x=14), 8 (row 2, x=8) and 4 (row 3,
      x=16), and the other six are track segments. (0.x drew them as `line`s, six tracks
      then three handles, and selected `line:nth-of-type(7|8|9)` — which on v1 matches
      nothing at all.) Directions alternate because the handles themselves do: row 1's
      sits at the RIGHT of its gap, row 2's at the LEFT, row 3's at the RIGHT.

      Because handles and tracks are now the same tag, a reorder here does not just
      permute which row leads — it can slide a TRACK. The keyed pin is what catches that.
    */
    ...on(icon('sliders-horizontal'), { animation: 'none' }),
    // Row 1 (y=5), handle at x=14.
    ...on(`${icon('sliders-horizontal')} path:nth-of-type(3)`, {
      animation: `veraNavSlideL 400ms ${EASE} 0ms`,
    }),
    // Row 2 (y=12), handle at x=8.
    ...on(`${icon('sliders-horizontal')} path:nth-of-type(8)`, {
      animation: `veraNavSlideR 400ms ${EASE} 70ms`,
    }),
    // Row 3 (y=19), handle at x=16.
    ...on(`${icon('sliders-horizontal')} path:nth-of-type(4)`, {
      animation: `veraNavSlideL 400ms ${EASE} 140ms`,
    }),

    /*
      Features — the knob throws to "on" and back.

      `circle` is the knob and is the only circle in the glyph, so this survives a
      reorder (v1 moved it from second to first and it held); the `rect` track is
      untouched and holds the knob's path. v1 did move the knob itself — see
      `veraNavToggle` for why the throw is now 6.
    */
    ...on(icon('toggle-left'), { animation: 'none' }),
    ...on(`${icon('toggle-left')} circle`, {
      animation: `veraNavToggle 460ms ${EASE_DELIBERATE}`,
    }),

    /*
      Business figures — two digits and the equals key, then the total pops out.

      lucide's order is [body rect, display line, tall equals key, then the seven round
      keys: (16,10) (12,10) (8,10) (12,14) (8,14) (12,18) (8,18)]. So the round keys are
      `path:nth-of-type(1..7)`, the tall key is `line:nth-of-type(2)`, and the DISPLAY is
      `line:nth-of-type(1)` — untouched, because a readout is the one part of a calculator
      that does not move when you work it.

      NOTE THE WHOLE-GLYPH RULE IS AN ANIMATION, NOT THE USUAL `animation: 'none'`
      SUPPRESSION, for the same reason `plug`'s is: here the glyph itself is one of the
      moving parts. It out-specifies the `.lucide` fallback (0,5,0 vs 0,4,0) rather than
      inheriting it, so the hop is this recipe's own number and not the generic 1.7px pop
      — see `veraNavReckon` for that arithmetic.

      THE ORDER IS THE GESTURE. Three keys struck 70ms apart — bottom-left digit, a middle
      digit, then the equals bar — is someone entering a small sum; the same three fired
      together is a glyph flickering, which is what "click buttons" would degrade to if
      the stagger were dropped as an implementation detail. Two digits and an operator is
      also the shortest sequence that reads as ARITHMETIC rather than as a keypad being
      poked: one key is a poke, three with a terminator is a calculation.

      The 70ms/220ms rhythm matches `palette` (70/360) and `chart-no-axes-column` (55/400)
      and that is the sharing this file's header expressly permits — the family is "a run
      across a set of like parts", the keyframes are unrelated, and a key going in is not
      a well rising or a bar dipping.

      THE HOP LANDS ON THE EQUALS KEY, BY CONSTRUCTION rather than by luck. `veraNavReckon`
      is delayed 80ms and peaks at 40% of 400ms = 240ms; the bar's strike peaks at
      140 + 45% of 220 = 239ms. So the calculator leaves the row at the instant the equals
      key bottoms out, which is what makes the hop read as the RESULT of the sequence
      instead of a second twitch running beside it. Same construction as the Certificates
      check being written across the badge's settle so the two land together.

      The 80ms lead-in is deliberate and is the only liberty taken with "jump like it does
      now": at 5 frames it is imperceptible as a delay, and it buys the alignment above.
      Starting the hop at 0 instead would have the bar pressed on the way DOWN.

      TOTAL 480ms — the hop's 80 + 400, the longest of the three tracks. Its neighbours in
      this nav run 460ms (Invoices' printing rules) to 620ms (Notifications' bell), so it
      sits mid-band and cannot read as a stutter against the row above or below it.

      REJECTED: flashing the DISPLAY when the equals key lands ("the answer appears"). It
      is the better story and it has nowhere to go — a result arriving on a rule anchored
      at its left edge is `veraNavPrint` (Invoices' receipt lines) at a different
      amplitude, and no two unrelated icons share a motion here. The hop already says the
      total came out, and it says it in the channel the user asked to keep.
    */
    ...on(icon('calculator'), { animation: `veraNavReckon 400ms ${EASE} 80ms` }),
    // (8,18) — the bottom-left digit, where the run starts. Each key pivots on its own
    // coordinate, so the dot grows about itself rather than drifting toward the centre.
    ...on(`${icon('calculator')} path:nth-of-type(7)`, {
      animation: `veraNavKeyTap 220ms ${EASE} 0ms`,
      ...viewBox('8px 18px'),
    }),
    // (12,14) — a middle digit.
    ...on(`${icon('calculator')} path:nth-of-type(4)`, {
      animation: `veraNavKeyTap 220ms ${EASE} 70ms`,
      ...viewBox('12px 14px'),
    }),
    // The tall equals key, (16,14)-(16,18) — its own midpoint, so it thickens about its
    // spine instead of swelling off one end.
    ...on(`${icon('calculator')} line:nth-of-type(2)`, {
      animation: `veraNavBarTap 220ms ${EASE} 140ms`,
      ...viewBox('16px 16px'),
    }),

    /*
      Profile — the figure leans in.

      A whole-glyph rotation, so it reads nothing inside the icon and carries no entry in
      the signature table.

      Serves BOTH the Settings nav's Profile row and the account chip menu's first row,
      whose label is "Profile" for a company user and "Account" for internal staff. One
      glyph, one concept ("you"), one recipe — the same sharing rule `shopping-bag` gets
      across Products and Orders.
    */
    ...on(icon('user'), {
      animation: `veraNavLean 480ms ${EASE_DELIBERATE}`,
      ...viewBox('12px 22px'),
    }),

    /*
      Log out — the arrow departs and the room stays.

      lucide-react v1's order is [arrowhead, shaft, room], all three `path`s, so the arrow
      is `path:nth-of-type(-n + 2)`. This is the failure the 0.x comment here predicted:
      0.x drew the arrow as a `polyline` + `line` beside the room's lone `path`, and the
      selector named those TYPES, which v1 left matching nothing — the arrow would have
      sat still. It is now positional, like the other all-`path` recipes, and the keyed pin
      is what keeps a reorder from sliding the room out instead.

      Both halves take one rule, with one origin in view-box coordinates, so the head and
      the shaft compress about the same point and the arrow stays an arrow. Splitting them
      would let the shaft slide out from under its own head.
    */
    ...on(icon('log-out'), { animation: 'none' }),
    ...on(`${icon('log-out')} path:nth-of-type(-n + 2)`, {
      animation: `veraNavDepart 460ms ${EASE_DELIBERATE}`,
      // The arrow's TIP, at (21,12) — the point everything contracts toward, and the
      // point whose 2.5px of headroom sets the translate. See the keyframe.
      ...viewBox('21px 12px'),
    }),

    /*
      ── Accessibility: the whole thing is off under reduced motion ────────────────

      Last in the object and `!important` on purpose. Every rule above is a hover-scoped
      descendant selector and therefore out-specifies a bare media-query block; matching
      them selector-for-selector would be thirty more lines that have to be kept in step
      with thirty above. One blanket kill cannot fall out of step.

      It targets the icons and their children, so it catches the whole-glyph recipes and
      the sub-element ones in the same rule.

      NOTHING MAY BE LEFT STUCK, which is why this clears two properties and not one.
      `stroke-dasharray` is set OUTSIDE a keyframe (the Certificates check), so killing
      `animation` alone would leave it dashed — harmless as it happens, since a dash the
      length of the path renders solid, but relying on that coincidence is not the same as
      not having the problem. `opacity` needs no reset: every recipe that fades
      (`veraNavArrive`, `veraNavStamp`) carries opacity only inside its
      keyframes, so with the animation gone the element renders at its natural value.
      Check that when adding a recipe — a property set outside a keyframe belongs here.
    */
    '@media (prefers-reduced-motion: reduce)': {
      '& .lucide, & .lucide *': {
        animation: 'none !important',
        strokeDasharray: 'none !important',
      },
    },
  }
}

/**
 * The sidebar rail's rows are MUI `ListItemButton`s. Applied to the Drawer — see
 * Sidebar.tsx.
 */
export const sidebarIconMotionSx: CSSObject = navIconMotionSx(NAV_ROW_LIST_ITEM)

/**
 * The Settings left nav's rows are MUI `MenuItem`s (via @repo/ui `SideNav`). Applied to
 * the nav COLUMN rather than the page, because `.MuiMenuItem-root` is the class every
 * Select dropdown in the app also renders — see the scoping note in this file's header.
 */
export const settingsNavIconMotionSx: CSSObject = navIconMotionSx(NAV_ROW_MENU_ITEM)

/**
 * The sidebar's ACCOUNT CHIP MENU — Profile/Account, Theme, Log out.
 *
 * Same row class as the Settings nav, so this is byte-identical to the export above and
 * exists for its NAME: importing `settingsNavIconMotionSx` into the account chip would
 * read as a mistake at the call site, and the next person to change one surface would
 * have no signal that they were changing two. Emotion dedupes the keyframes by name, so
 * the cost is a second copy of the selector text.
 *
 * ── WHY THIS MENU AND NOT THE HELP POPOVER ──────────────────────────────────────
 * The Help popover was excluded BY CONSTRUCTION when the rail's motion was built (see
 * Sidebar.tsx: Drawer-scoped, so it cannot reach a menu that portals out), and it stays
 * excluded deliberately rather than by oversight.
 *
 * A MENU IS TRAVERSED. You sweep the cursor down past several rows to reach the one you
 * want, so every row you pass fires on the way — which reads as a stutter rather than a
 * greeting. That cost scales with the row count, and it is the whole argument:
 *
 *   - Help has ~11 rows across three groups. Reaching Status from the top fires ten
 *     animations nobody asked for.
 *   - This menu has three. The traversal cost is one row, at most.
 *
 * And the account menu has a reason to move that Help does not: its rows lead to
 * surfaces that now animate — the first row opens the Settings nav (whose `user` row
 * leans, from this same table), and the Theme row names the concept the Appearance row
 * wipes for. A rail row animating into a menu whose rows sit dead is a visible seam
 * between two halves of one gesture.
 *
 * So this is a deliberate single exception, NOT the first step of a sweep. Do not extend
 * it to the Help popover, to `ActionDropdown` kebabs, or to any other menu; popovers are
 * the hinge where this reaches dozens of surfaces and stops meaning anything.
 *
 * ── HOW THE SCOPE HOLDS ─────────────────────────────────────────────────────────
 * Applied to THIS `Menu`'s own `Paper` (`slotProps.paper.sx`), and every selector in the
 * table is a descendant of it. Containment is therefore STRUCTURAL rather than a matter
 * of selector precision: emotion stamps a class on one Paper element, and the Help
 * popover is a different `Menu` with a different Paper in a sibling portal. There is no
 * common ancestor below `<body>` for a rule to leak through, whatever classes the two
 * happen to share (`.MuiMenuItem-root` is on both).
 *
 * ⚠️ Do NOT hoist this into `SidebarUserBlock`'s shared `menuPaperSx` — the Theme
 * SUBMENU spreads that same object, so hoisting would silently animate a fourth surface
 * this was never scoped to.
 */
export const accountMenuIconMotionSx: CSSObject = navIconMotionSx(NAV_ROW_MENU_ITEM)

/**
 * The account chip menu's THEME SUBMENU — Light / Dark / System.
 *
 * A FOURTH export of the same object, for the same reason the third one exists: the name
 * is the point. The submenu is a separate `Menu` with a separate Paper, so it needs its
 * own application of the table, and the ⚠️ on `accountMenuIconMotionSx` above says
 * explicitly why it must NOT come from hoisting the motion into the shared
 * `menuPaperSx` — that object is spread by both papers, so hoisting animates whichever
 * surface happens to spread it next rather than the one that was reasoned about.
 *
 * ── WHY THIS SUBMENU IS IN SCOPE WHEN THE HELP POPOVER IS NOT ───────────────────
 * The exclusion argument above is about TRAVERSAL COST: a menu you sweep down past ten
 * rows fires ten animations nobody asked for. This submenu has three rows and is entered
 * sideways, from a row that already animates — so the traversal cost is at most one row,
 * exactly as for its parent.
 *
 * It also fails the OTHER half of the Help argument. The parent menu was included partly
 * because "a rail row animating into a menu whose rows sit dead is a visible seam between
 * two halves of one gesture". The Theme row's chevron beckons rightward toward this
 * paper; landing on three motionless glyphs was that same seam, one level further in.
 *
 * Still not the start of a sweep. Kebabs and the Help popover stay out.
 */
export const themeSubmenuIconMotionSx: CSSObject = navIconMotionSx(NAV_ROW_MENU_ITEM)
